"use client"

import { toast } from "sonner"
import { DEBUG, debugLog, debugGroup, debugGroupEnd, debugError } from '@/lib/debug'
import { cacheAreas, cacheUsers, cacheScenarios } from '@/lib/workspace-catalog'
import { withOrderedSteps } from '@/lib/scenario-modules'

// 🌐 Базовый URL API v3 — сервер переехал на dev.api.casper-ai.ru (см. docs/API/v3.json → servers)
const API_BASE_URL = process.env.NEXT_PUBLIC_V3_API_URL

// ===============================
// 📝 Типы данных (API v3 схемы)
// ===============================

export interface LoginRequest {
  login: string
  password: string
}

export interface LoginResponse {
  accessToken: string
  refreshToken: string
  expiresIn: number
}

export interface RoleV3 {
  id: number
  name: string
  workspaceId?: number | null
  predefinedRole?: number | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface UserV3 {
  id: number
  name: string
  login: string
  workPosition?: string | null
  description?: string | null
  phoneNumber?: string | null
  photoId?: number | null
  photo?: { id: number; url?: string | null; staticUrl?: string | null } | null
  parentWorkspaceId?: number | null
  systemPermission?: number
  lastLogin?: string
  created?: string
  updated?: string
  objectState?: number
  version?: number
  creatorId?: number | null
  userWorkspaces?: UserWorkspaceV3[]
}

export interface GroupV3 {
  id: number
  name: string
  workspaceId: number
  roleId?: number | null
  imageId?: number | null
  creatorId?: number | null
  creator?: { id?: number; name?: string; login?: string } | null
  description?: string | null
  created?: string
  updated?: string
  objectState?: number
  objectStatusId?: number | null
  version?: number
  /** Группа может быть выбрана ответственной за инцидент */
  allowBeIncidentsResolver?: boolean
  /** Группа может быть выбрана утверждающей по инциденту */
  allowBeIncidentsApprover?: boolean
  /** Группа может быть выбрана утверждающей по процедуре */
  allowBeScenariosApprover?: boolean
}

export interface UserGroupV3 {
  userId: number
  groupId: number
  /** Может приходить в ответе /workspaces/groups/{id}/users */
  user?: { id?: number; name?: string; login?: string } | null
  group?: GroupV3
  roleId?: number | null
  role?: RoleV3 | null
  creatorId?: number | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface WorkspaceV3 {
  id: number
  name: string
  rootFolderId?: number | null
  imageId?: number | null
  aiId?: string | null
  disableAi?: boolean
  maxAllowedUsers?: number
  creatorId?: number | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface UserWorkspaceV3 {
  userId: number
  workspaceId: number
  workspace?: WorkspaceV3 | null
  /** Профиль пользователя (приходит в GET /workspaces/{id}/users — не нужен отдельный GET по userId) */
  user?: UserV3 | null
  roleId: number
  role?: RoleV3 | null
  /** Может ли пользователь быть выбран утверждающим инцидента */
  allowBeIncidentsApprover?: boolean
  /** Может ли пользователь быть выбран ответственным за инцидент */
  allowBeIncidentsResolver?: boolean
  /** Может ли пользователь быть выбран утверждающим процедуры */
  allowBeScenariosApprover?: boolean
  creatorId?: number | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

/**
 * Дубликаты форм из legacy main API (lib/api.ts — User / Group / UserGroup)
 * для UI (permissions, workspace, user-context) без импорта старого клиента.
 */
export interface PermissionUser {
  id: number
  name: string
  login: string
  role: number
  objectState?: number
  lastLogin?: string
  created?: string
  updated?: string
}

export interface PermissionGroup {
  id: number
  name: string
  assistantId: number | null
  imageId: number | null
  disableAi: boolean
  objectState?: number
  ragIndex?: string | null
}

export interface PermissionUserGroup {
  userId: number
  user: PermissionUser
  groupId: number
  group: PermissionGroup
  joinedAt: string
  role: number
  invitedByUser: number
  objectState?: number
}

// ===============================
// ❌ Ошибка API
// ===============================

export class ApiError extends Error {
  public status: number
  public data: any

  constructor(status: number, message: string, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

// ===============================
// 🔒 Глобальный обработчик 401
// ===============================

type UnauthorizedHandler = () => void
let _unauthorizedHandler: UnauthorizedHandler | null = null

export const setUnauthorizedHandler = (handler: UnauthorizedHandler | null): void => {
  _unauthorizedHandler = handler
}

// ===============================
// 🔧 Базовый запрос к API v3
// ===============================

const apiRequest = async (endpoint: string, options: RequestInit = {}, expectBlob = false): Promise<any> => {
  const url = `${API_BASE_URL}${endpoint}`
  const method = options.method || 'GET'

  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...options.headers,
  }

  if (DEBUG.shouldLog('api')) {
    debugGroup('api', `🚀 API v3 REQUEST: ${method} ${endpoint}`)
    debugLog('api', '📍 URL:', url)
    debugLog('api', '🔧 Method:', method)
    debugLog('api', '📋 Headers:', headers)
    if (options.body) {
      debugLog('api', '📦 Body:', options.body)
      try {
        debugLog('api', '📄 Parsed Body:', JSON.parse(options.body as string))
      } catch {
        debugLog('api', '📄 Raw Body:', options.body)
      }
    }
    debugGroupEnd('api')
  } else {
    debugLog('api', `📡 v3 ${method} ${url}`)
  }

  try {
    const response = await fetch(url, { ...options, headers })

    // Собираем заголовки синхронно — до любых await
    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => { responseHeaders[key] = value })

    if (!response.ok) {
      const errorText = await response.text()
      let parsed: any = null
      let messageFromServer = ''
      try { parsed = JSON.parse(errorText) } catch { parsed = null }

      if (parsed && typeof parsed === 'object') {
        messageFromServer = (parsed.message && String(parsed.message)) || JSON.stringify(parsed)
      } else {
        messageFromServer = errorText?.trim() || `HTTP ${response.status}`
      }

      if (DEBUG.shouldLog('api')) {
        debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
        debugLog('api', '📊 Status:', response.status)
        debugLog('api', '🔗 URL:', response.url)
        debugLog('api', '✅ OK:', response.ok)
        debugLog('api', '📋 Response Headers:', responseHeaders)
        debugLog('api', '❌ Error Body (raw):', errorText)
        if (parsed !== null) debugLog('api', '❌ Error Data (parsed):', parsed)
        debugGroupEnd('api')
      }

      if (response.status === 401 && endpoint !== '/auth/login') {
        _unauthorizedHandler?.()
      }

      throw new ApiError(response.status, messageFromServer, parsed ?? { raw: errorText })
    }

    if (expectBlob) {
      const data = await response.blob()
      if (DEBUG.shouldLog('api')) {
        debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
        debugLog('api', '📊 Status:', response.status)
        debugLog('api', '🔗 URL:', response.url)
        debugLog('api', '✅ OK:', response.ok)
        debugLog('api', '📋 Response Headers:', responseHeaders)
        debugLog('api', '✅ Response Blob:', { size: data.size, type: data.type })
        debugGroupEnd('api')
      }
      return data
    }

    const contentLength = response.headers.get('content-length')
    if (contentLength === '0') {
      if (DEBUG.shouldLog('api')) {
        debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
        debugLog('api', '📊 Status:', response.status)
        debugLog('api', '🔗 URL:', response.url)
        debugLog('api', '✅ OK:', response.ok)
        debugLog('api', '📋 Response Headers:', responseHeaders)
        debugLog('api', '✅ Response: Пустое тело')
        debugGroupEnd('api')
      }
      return null
    }

    try {
      const data = await response.json()
      if (DEBUG.shouldLog('api')) {
        debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
        debugLog('api', '📊 Status:', response.status)
        debugLog('api', '🔗 URL:', response.url)
        debugLog('api', '✅ OK:', response.ok)
        debugLog('api', '📋 Response Headers:', responseHeaders)
        debugLog('api', '✅ Response Data:', data)
        debugGroupEnd('api')
      }
      return data
    } catch {
      try {
        const text = await response.text()
        if (!text || text.trim() === '') {
          if (DEBUG.shouldLog('api')) {
            debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
            debugLog('api', '📊 Status:', response.status)
            debugLog('api', '🔗 URL:', response.url)
            debugLog('api', '✅ OK:', response.ok)
            debugLog('api', '📋 Response Headers:', responseHeaders)
            debugLog('api', '✅ Response: Пустое тело')
            debugGroupEnd('api')
          }
          return null
        }
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
          debugLog('api', '📊 Status:', response.status)
          debugLog('api', '🔗 URL:', response.url)
          debugLog('api', '✅ OK:', response.ok)
          debugLog('api', '📋 Response Headers:', responseHeaders)
          debugLog('api', '⚠️ Не JSON, возвращаем текст:', text)
          debugGroupEnd('api')
        }
        return text
      } catch {
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: ${method} ${endpoint} → ${response.status}`)
          debugLog('api', '📊 Status:', response.status)
          debugLog('api', '🔗 URL:', response.url)
          debugLog('api', '✅ OK:', response.ok)
          debugLog('api', '📋 Response Headers:', responseHeaders)
          debugLog('api', '✅ Response: Не удалось прочитать тело')
          debugGroupEnd('api')
        }
        return null
      }
    }
  } catch (error) {
    // AbortError — намеренная отмена запроса (cleanup при размонтировании), не логируем
    if (error instanceof Error && error.name === 'AbortError') throw error
    if (DEBUG.shouldLog('api') && !(error instanceof ApiError)) {
      debugGroup('api', '💥 NETWORK ERROR (v3)')
      debugError('Error object:', error)
      debugError('Error message:', error instanceof Error ? error.message : String(error))
      debugError('Error stack:', error instanceof Error ? error.stack : 'No stack')
      debugGroupEnd('api')
    }
    if (error instanceof ApiError) throw error
    console.error(`💥 Сетевая ошибка (v3):`, error)
    toast.error("Сетевая ошибка", { description: "Проверьте подключение к интернету", duration: 5000 })
    throw new Error(`Не удалось подключиться к серверу: ${error}`)
  }
}

// ===============================
// 🔑 Auth (POST /auth/login)
// ===============================

export const login = async (credentials: LoginRequest): Promise<LoginResponse> => {
  return apiRequest('/auth/login', {
    method: 'POST',
    body: JSON.stringify(credentials),
  })
}

// Photo authorization error codes:
// 0 - None (success)
// 1 - PersonNotFound
// 2 - MultiplePeopleDetected
// 3 - MatchNotFound
// 4 - FaceTooSmall
export interface PhotoAuthorizationResult {
  errorCode: number
  tokenContainer: LoginResponse | null
}

export const loginByPhoto = async (photoFile: File): Promise<PhotoAuthorizationResult> => {
  const formData = new FormData()
  // Field name must match the ASP.NET Core IFormFile parameter name in the controller.
  // The endpoint is /auth/login/photo → parameter is most likely "photo".
  formData.append('photo', photoFile, photoFile.name)
  return apiRequest('/auth/login/photo', {
    method: 'POST',
    body: formData,
  })
}

export interface SignUpRequest {
  name: string
  login: string
  password: string
}

export const signUp = async (data: SignUpRequest): Promise<UserV3> => {
  return apiRequest('/auth/signup', {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

// ===============================
// 👤 User (API v3)
// ===============================

export const getUser = async (accessToken: string): Promise<UserV3> => {
  return apiRequest('/user', {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const getUserById = async (accessToken: string, id: number): Promise<UserV3> => {
  return apiRequest(`/user/${id}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface UserUpdateRequestV3 {
  name?: string | null
  login?: string | null
  password?: string | null
  workPosition?: string | null
  description?: string | null
  phoneNumber?: string | null
  photoId?: number | null
}

export const updateUser = async (
  accessToken: string,
  userId: number,
  data: UserUpdateRequestV3
): Promise<UserV3> => {
  return apiRequest(`/user/update?id=${userId}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export interface UserRegisterRequestV3 {
  name: string
  login: string
  password: string
}

export const createUser = async (
  accessToken: string,
  data: UserRegisterRequestV3
): Promise<UserV3> => {
  return apiRequest('/user/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const getAllUsers = async (
  accessToken: string,
  params: { skip?: number; take?: number } = {}
): Promise<UserV3[]> => {
  const query = new URLSearchParams()
  if (params.skip !== undefined) query.set('skip', String(params.skip))
  if (params.take !== undefined) query.set('take', String(params.take))
  const qs = query.toString() ? `?${query.toString()}` : ''
  return apiRequest(`/users/all${qs}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

// ===============================
// 👥 Groups (API v3: /workspaces/groups/*)
// ===============================

export const getGroups = async (
  accessToken: string
): Promise<UserGroupV3[]> => {
  return apiRequest(`/groups`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const getAllGroups = async (
  accessToken: string,
  params: { skip?: number; take?: number } = {}
): Promise<GroupV3[]> => {
  const query = new URLSearchParams()
  if (params.skip !== undefined) query.set('skip', String(params.skip))
  if (params.take !== undefined) query.set('take', String(params.take))
  const qs = query.toString() ? `?${query.toString()}` : ''
  return apiRequest(`/workspaces/groups/all${qs}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/**
 * GET /api/v3/workspaces/{workspaceId}/groups
 * Получить все группы воркспейса (доступно обычным пользователям)
 */
export const getWorkspaceGroups = async (
  accessToken: string,
  workspaceId: number
): Promise<GroupV3[]> => {
  const groups: GroupV3[] = await apiRequest(`/workspaces/${workspaceId}/groups`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
  return groups.filter((g) => g.objectState !== 2)
}

export interface GroupCreateRequestV3 {
  name: string
  workspaceId: number
  description?: string | null
  imageId?: number | null
  roleId?: number | null
}

export const createGroup = async (
  accessToken: string,
  data: GroupCreateRequestV3
): Promise<GroupV3> => {
  return apiRequest('/workspaces/groups/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export interface GroupUpdateRequestV3 {
  name?: string | null
  description?: string | null
  imageId?: number | null
  allowBeIncidentsResolver?: boolean
  allowBeIncidentsApprover?: boolean
  allowBeScenariosApprover?: boolean
}

export const updateGroup = async (
  accessToken: string,
  groupId: number,
  data: GroupUpdateRequestV3
): Promise<GroupV3> => {
  return apiRequest(`/workspaces/groups/${groupId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ===============================
// 📁 Folders (API v3: /folders/*)
// ===============================

/** extension: 0=None, 1=Area, 2=Scenario, 3=Note, 4=File */
export interface FolderEntityItemV3 {
  id: number
  extension?: number
  folderId: number
  fileId?: number | null
  file?: FileEntityV3 | null
  areaId?: number | null
  area?: Area | null
  scenarioId?: number | null
  scenario?: Scenario | null
  entityId?: number
  entityName?: string | null
  created?: string
  objectState?: number
  version?: number
}

export interface FolderV3 {
  id: number
  name: string
  workspaceId: number
  parentId?: number | null
  isPublic?: boolean
  children?: FolderV3[]
  entities?: FolderEntityItemV3[]
  creatorId?: number | null
  creator?: { id?: number; name?: string; login?: string } | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface FolderCreateRequestV3 {
  name: string
  workspaceId: number
  parentId?: number | null
  isPublic?: boolean | null
}

export const createFolder = async (
  accessToken: string,
  data: FolderCreateRequestV3
): Promise<FolderV3> => {
  return apiRequest('/folders/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteFolder = async (
  accessToken: string,
  folderId: number
): Promise<void> => {
  return apiRequest(`/folders/${folderId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface GetFolderParams {
  sort?: number      // >= 0 ascending; < 0 descending
  skip?: number
  take?: number
  property?: string  // 'id' | 'name' | 'created' | 'size'
  filter?: number    // 0-None; 1-Area; 2-Scenario; 3-Note; 4-File
}

export const getFolder = async (
  accessToken: string,
  folderId: number,
  params?: GetFolderParams
): Promise<FolderV3> => {
  let qs = ''
  if (params) {
    const p = new URLSearchParams()
    if (params.sort !== undefined) p.set('sort', String(params.sort))
    if (params.skip !== undefined) p.set('skip', String(params.skip))
    if (params.take !== undefined) p.set('take', String(params.take))
    if (params.property) p.set('property', params.property)
    if (params.filter !== undefined) p.set('filter', String(params.filter))
    qs = p.toString() ? `?${p.toString()}` : ''
  }
  return apiRequest(`/folders/${folderId}${qs}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

// NOTE: V3 API does not expose a group delete endpoint yet.
// This function is a placeholder — call it when the endpoint becomes available.
export const deleteGroup = async (
  accessToken: string,
  groupId: number
): Promise<void> => {
  return apiRequest(`/workspaces/groups/${groupId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const getGroupUsers = async (
  accessToken: string,
  groupId: number
): Promise<UserGroupV3[]> => {
  return apiRequest(`/workspaces/groups/${groupId}/users`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface GroupInviteRequestV3 {
  userId: number
  roleId?: number | null
}

export const inviteUserToGroup = async (
  accessToken: string,
  groupId: number,
  data: GroupInviteRequestV3
): Promise<UserGroupV3> => {
  return apiRequest(`/workspaces/groups/${groupId}/invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const kickUserFromGroup = async (
  accessToken: string,
  groupId: number,
  userId: number
): Promise<void> => {
  return apiRequest(`/workspaces/groups/${groupId}/kick`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId }),
  })
}

export const leaveGroup = async (
  accessToken: string,
  groupId: number
): Promise<void> => {
  return apiRequest(`/workspaces/groups/${groupId}/leave`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

// updateUserRoleInGroup: userId теперь в теле запроса (не в URL), поле — roleId (не role)
export const updateUserRoleInGroup = async (
  accessToken: string,
  groupId: number,
  userId: number,
  roleId: number | null
): Promise<UserGroupV3> => {
  return apiRequest(`/workspaces/groups/${groupId}/user`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId, roleId }),
  })
}

// ===============================
// 🏢 Workspaces (API v3: /workspaces/*)
// ===============================

/**
 * Параллельные GET с тем же путём+query+токеном объединяются в один сетевой запрос
 * (React Strict Mode: двойной mount; два эффекта с одними deps).
 */
const inFlightParallelGetByKey = new Map<string, Promise<unknown>>()

function dedupeParallelGet<T>(
  accessToken: string,
  pathWithQuery: string,
  run: () => Promise<T>
): Promise<T> {
  const key = `${accessToken.slice(0, 32)}:${pathWithQuery}`
  const existing = inFlightParallelGetByKey.get(key) as Promise<T> | undefined
  if (existing) return existing
  const p = run().finally(() => {
    if (inFlightParallelGetByKey.get(key) === p) inFlightParallelGetByKey.delete(key)
  })
  inFlightParallelGetByKey.set(key, p)
  return p as Promise<T>
}

/** Сливаем параллельные GET /workspaces с одним токеном в один сетевой запрос */
const inFlightWorkspacesByToken = new Map<string, Promise<UserWorkspaceV3[]>>()

/** Короткий TTL: иначе второй вызов после завершения первого снова бьёт в сеть (analytics + chart + context). */
const workspacesListCacheByToken = new Map<
  string,
  { data: UserWorkspaceV3[]; expiresAt: number }
>()
const WORKSPACES_LIST_CACHE_TTL_MS = 20_000

/** Сброс кэша списка (например после создания workspace), иначе до истечения TTL вернётся старый список. */
export function invalidateGetWorkspacesCache(accessToken?: string) {
  if (accessToken) workspacesListCacheByToken.delete(accessToken)
  else workspacesListCacheByToken.clear()
}

export const getWorkspaces = async (accessToken: string): Promise<UserWorkspaceV3[]> => {
  const now = Date.now()
  const cached = workspacesListCacheByToken.get(accessToken)
  if (cached && cached.expiresAt > now) {
    return Promise.resolve(cached.data)
  }

  const existing = inFlightWorkspacesByToken.get(accessToken)
  if (existing) return existing

  const p = apiRequest('/workspaces', {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
    .then((data: UserWorkspaceV3[]) => {
      const list = Array.isArray(data) ? data : []
      workspacesListCacheByToken.set(accessToken, {
        data: list,
        expiresAt: Date.now() + WORKSPACES_LIST_CACHE_TTL_MS,
      })
      return list
    })
    .finally(() => {
      if (inFlightWorkspacesByToken.get(accessToken) === p) {
        inFlightWorkspacesByToken.delete(accessToken)
      }
    })
  inFlightWorkspacesByToken.set(accessToken, p)
  return p
}

export const getWorkspaceById = async (
  accessToken: string,
  workspaceId: number
): Promise<WorkspaceV3> => {
  return apiRequest(`/workspaces/${workspaceId}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const getAllWorkspaces = async (
  accessToken: string,
  params: { skip?: number; take?: number } = {}
): Promise<WorkspaceV3[]> => {
  const query = new URLSearchParams()
  if (params.skip !== undefined) query.set('skip', String(params.skip))
  if (params.take !== undefined) query.set('take', String(params.take))
  const qs = query.toString() ? `?${query.toString()}` : ''
  return apiRequest(`/workspaces/all${qs}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface WorkspaceCreateRequestV3 {
  name: string
  imageId?: number | null
}

export const createWorkspace = async (
  accessToken: string,
  data: WorkspaceCreateRequestV3
): Promise<WorkspaceV3> => {
  return apiRequest('/workspaces/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export interface WorkspaceUpdateRequestV3 {
  name?: string | null
  imageId?: number | null
}

export const updateWorkspace = async (
  accessToken: string,
  workspaceId: number,
  data: WorkspaceUpdateRequestV3
): Promise<WorkspaceV3> => {
  return apiRequest(`/workspaces/${workspaceId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const getWorkspaceUsers = async (
  accessToken: string,
  workspaceId: number,
  params: { skip?: number; take?: number; query?: string } = {}
): Promise<UserWorkspaceV3[]> => {
  const searchParams = new URLSearchParams()
  if (params.skip !== undefined) searchParams.set('skip', String(params.skip))
  if (params.take !== undefined) searchParams.set('take', String(params.take))
  if (params.query) searchParams.set('query', params.query)
  const qs = searchParams.toString() ? `?${searchParams.toString()}` : ''
  const pathWithQuery = `/workspaces/${workspaceId}/users${qs}`
  const result = await dedupeParallelGet(accessToken, pathWithQuery, () =>
    apiRequest(pathWithQuery, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${accessToken}` },
    })
  )
  // Авто-кэшируем справочник пользователей для мгновенного отображения имён
  try { cacheUsers(workspaceId, result) } catch {}
  return result
}

export interface WorkspaceInviteRequestV3 {
  userId: number
  roleId?: number | null
}

export const inviteUserToWorkspace = async (
  accessToken: string,
  workspaceId: number,
  data: WorkspaceInviteRequestV3
): Promise<void> => {
  return apiRequest(`/workspaces/${workspaceId}/invite`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const kickUserFromWorkspace = async (
  accessToken: string,
  workspaceId: number,
  userId: number
): Promise<void> => {
  return apiRequest(`/workspaces/${workspaceId}/user/${userId}/kick`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const leaveWorkspace = async (
  accessToken: string,
  workspaceId: number
): Promise<void> => {
  return apiRequest(`/workspaces/${workspaceId}/leave`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface UpdateUserWorkspaceRequestV3 {
  roleId?: number | null
  predefinedRole?: number | null
  /** Разрешить пользователю быть утверждающим инцидента */
  allowBeIncidentsApprover?: boolean | null
  /** Разрешить пользователю быть ответственным за инцидент */
  allowBeIncidentsResolver?: boolean | null
  /** Разрешить пользователю быть утверждающим процедуры */
  allowBeScenariosApprover?: boolean | null
  name?: string | null
  login?: string | null
  password?: string | null
  workPosition?: string | null
  photoId?: number | null
}

export const updateUserInWorkspace = async (
  accessToken: string,
  workspaceId: number,
  userId: number,
  data: UpdateUserWorkspaceRequestV3
): Promise<UserWorkspaceV3> => {
  return apiRequest(`/workspaces/${workspaceId}/user/${userId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const createUserInWorkspace = async (
  accessToken: string,
  workspaceId: number,
  data: UserRegisterRequestV3
): Promise<UserV3> => {
  return apiRequest(`/workspaces/${workspaceId}/user/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ===============================
// 🎭 Roles (API v3)
// ===============================

// PredefinedRole: 0-None, 1-Root, 2-AsIs, 3-Admin, 4-User, 5-Creator, 6-Guest, 7-Manager, 8-Moderator, 9-ContentCreator
export const PREDEFINED_ROLES = {
  0: 'None',
  1: 'Root',
  2: 'AsIs',
  3: 'Admin',
  4: 'User',
  5: 'Creator',
  6: 'Guest',
  7: 'Manager',
  8: 'Moderator',
  9: 'ContentCreator',
} as const

export const getSystemRoles = async (accessToken: string): Promise<RoleV3[]> => {
  return apiRequest('/roles/system', {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const getWorkspaceRoles = async (
  accessToken: string,
  workspaceId: number
): Promise<RoleV3[]> => {
  return apiRequest(`/workspaces/${workspaceId}/roles`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export const updateUserPredefinedRole = async (
  accessToken: string,
  workspaceId: number,
  userId: number,
  predefinedRole: number
): Promise<UserWorkspaceV3> => {
  return apiRequest(`/workspaces/${workspaceId}/user/${userId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ predefinedRole }),
  })
}

// ===============================
// 📊 Analytics & instruction reads (API v3)
// ===============================

/** Query: workspace = legacy group id для области данных */
export interface AnalyticsParams {
  workspace?: number
  /** @deprecated то же, что workspace */
  group?: number
  begin?: number
  end?: number
  today?: number
  year?: number
  month?: number
  day?: number
  user?: number | number[]
  from?: number
  to?: number
  scenario?: number
  count?: number
  event?: string | string[]
  type?: number
  skip?: number
  take?: number
  sort?: number
  query?: string | string[]
  ignoreAdmins?: boolean
  ignoreDismissed?: boolean
  session?: number
}

export interface ScenarioRankItem {
  idStr: string | null
  count: number
}

export interface AnalyticsEvent {
  id: number
  userId: number
  userLogin?: string
  name: string
  time: number
  timeZone: number
  type: number
  value: any
  groupId: number | null
  deviceId: string | null
  platform: string | null
  userAgent: string | null
  sessionId: number | null
}

export interface AnalyticsSession {
  id: number
  userId: number
  userLogin: string | null
  groupId: number | null
  deviceId: string | null
  platform: number | null
  userAgent: string | null
  timeZone: number
  beginTime: number
  endTime: number
  isClosed: boolean
  isPaused: boolean
}

export interface ScenarioReport {
  id: number
  instructionId: number
  scenarioId: number
  scenarioName?: string
  instructionName?: string
  areaName?: string | null
  userId: number
  userLogin?: string
  userName?: string
  dataId: number
  startTime: number
  endTime: number
  totalTime: number
  dataJson?: string
  completedSteps?: number | null
  totalSteps?: number | null
  isCompleted?: boolean | null
}

export interface ScenarioReportsResponse {
  totalCount: number
  result: ScenarioReport[]
}

/** Версия процедуры-файла, по которой прошли отчёт: коммит и снапшот файла (в `data` — JSON с `assetId`) */
export interface FileReportVersion {
  fileId: number
  commitId: number
  snapshotId: number
  data: string
}

export interface FullScenarioReport {
  id: number
  areaId?: number | null
  /** У отчётов по процедурам-файлам (`.cspr`) — null: такой отчёт привязан к файлу (`fileId`) */
  scenarioId: number
  scenarioSnapshotId?: number | null
  /** Отчёт по процедуре-файлу (FileReports): файл, коммит его версии и название */
  fileId?: number | null
  fileCommitId?: number | null
  fileName?: string | null
  fileDeleted?: boolean | null
  fileVersion?: FileReportVersion | null
  userId: number
  /** Статус отчёта: 0=Created, 1=InProgress, 2=Paused, 3=Sent */
  status?: number | null
  dataId: number
  startTime: number
  endTime: number
  totalTime: number
  dataJson?: string
  areaName?: string | null
  scenarioName: string
  userLogin: string
  userName?: string
  completedSteps?: number | null
  totalSteps?: number | null
  isCompleted?: boolean | null
  areaDeleted?: boolean | null
  scenarioDeleted?: boolean | null
}

export interface SnapshotV3 {
  id: number
  data: string
  creatorId?: number | null
  creator?: { id?: number; name?: string; login?: string } | null
  created?: string
  updated?: string
  objectState?: number
  objectStatusId?: number | null
  description?: string | null
  version?: number
}

export interface ScenarioReportsWithSnapshots {
  totalCount: number
  result: FullScenarioReport[]
  scenarioSnapshots: SnapshotV3[]
  /** Коммиты и снапшоты версий процедур-файлов, по которым прошли отчёты из выборки */
  fileCommits?: Commit[]
  fileSnapshots?: SnapshotV3[]
}

function workspaceIdFromAnalyticsParams(params: AnalyticsParams): number | undefined {
  return params.workspace !== undefined ? params.workspace : params.group
}

function appendAnalyticsWorkspace(qs: URLSearchParams, params: AnalyticsParams): void {
  const id = workspaceIdFromAnalyticsParams(params)
  if (id !== undefined) qs.append('workspace', String(id))
}

export const downloadMetrics = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<Blob> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.today !== undefined) queryParams.append('today', params.today.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/download${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  }, true)
}

export const downloadFile = (blob: Blob, filename: string): void => {
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.style.display = 'none'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
  debugLog('api', `📁 Файл скачан: ${filename} (${blob.size} bytes)`)
}

export const getDailyActiveUsers = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.year !== undefined) queryParams.append('year', params.year.toString())
  if (params.month !== undefined) queryParams.append('month', params.month.toString())
  if (params.day !== undefined) queryParams.append('day', params.day.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/dau${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getMonthlyActiveUsers = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.year !== undefined) queryParams.append('year', params.year.toString())
  if (params.month !== undefined) queryParams.append('month', params.month.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/mau${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getUserLaunches = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/user/launches${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getAverageTime = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/time/average/users${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getActiveUsers = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/au${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getScenariosCount = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.scenario !== undefined) queryParams.append('scenario', params.scenario.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getUserScenarios = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/user/scenarios${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getScenariosRank = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<ScenarioRankItem[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.from !== undefined) queryParams.append('from', params.from.toString())
  if (params.to !== undefined) queryParams.append('to', params.to.toString())
  if (params.count !== undefined) queryParams.append('count', params.count.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/rank${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getScenariosRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/range${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getApplicationLaunchesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/launches/range${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getActiveInstructionsRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/instructions${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getActiveScenariosRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/scenarios${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getScenariosChangesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/scenarios/changes${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** Диапазон по дням — в OpenAPI v3: GET .../copilot/range */
export const getCopilotRequestsRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/copilot/range${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getActiveAiFilesRange = async (
  accessToken: string,
  params: AnalyticsParams = {}
): Promise<number[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/metrics/active/ai/files${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getEvents = async (
  accessToken: string,
  params: AnalyticsParams = {},
  controller?: AbortController
): Promise<AnalyticsEvent[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.event !== undefined) {
    if (Array.isArray(params.event)) {
      params.event.forEach((e) => {
        if (e && e.trim() !== '') queryParams.append('event', e)
      })
    } else if (params.event.trim() !== '') {
      queryParams.append('event', params.event)
    }
  }
  if (params.type !== undefined) queryParams.append('type', params.type.toString())
  if (params.user !== undefined) {
    if (Array.isArray(params.user)) {
      params.user.forEach((u) => queryParams.append('user', u.toString()))
    } else {
      queryParams.append('user', params.user.toString())
    }
  }
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.query !== undefined) {
    if (Array.isArray(params.query)) {
      params.query.forEach((q) => {
        if (q && q.trim() !== '') queryParams.append('query', q)
      })
    } else if (params.query.trim() !== '') {
      queryParams.append('query', params.query)
    }
  }
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  if (params.session !== undefined) queryParams.append('session', params.session.toString())
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: controller?.signal,
  })
}

export const getSessions = async (
  accessToken: string,
  params: AnalyticsParams = {},
  controller?: AbortController
): Promise<AnalyticsSession[]> => {
  const queryParams = new URLSearchParams()
  appendAnalyticsWorkspace(queryParams, params)
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.ignoreAdmins !== undefined) queryParams.append('ignoreAdmins', String(params.ignoreAdmins))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  return apiRequest(`/analytics/sessions${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
    signal: controller?.signal,
  })
}

export const getWorkspaceScenarioReports = async (
  accessToken: string,
  workspaceId: number,
  params: {
    sort?: number
    skip?: number
    take?: number
    begin?: number
    end?: number
    scenario?: number
    user?: number
    statuses?: number[]
    type?: number[]
  } = {}
): Promise<ScenarioReportsResponse | unknown> => {
  const queryParams = new URLSearchParams()
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.scenario !== undefined) queryParams.append('scenario', params.scenario.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  params.statuses?.forEach(s => queryParams.append('statuses', s.toString()))
  params.type?.forEach(t => queryParams.append('type', t.toString()))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  const pathWithQuery = `/workspaces/${workspaceId}/reports${query}`
  return dedupeParallelGet(accessToken, pathWithQuery, () =>
    apiRequest(pathWithQuery, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
}

/**
 * GET /api/v3/workspaces/{workspaceId}/reports-with-snapshots — отчёты процедур со снепшотами:
 * и по процедурам API v3, и по процедурам-файлам `.cspr` (`fileId`, `fileCommitId`, `fileSnapshots`).
 */
export const getWorkspaceReportsWithSnapshots = async (
  accessToken: string,
  workspaceId: number,
  params: {
    sort?: number
    skip?: number
    take?: number
    begin?: number
    end?: number
    scenario?: number
    /** Только отчёты процедуры-файла с этим ID */
    file?: number
    user?: number
    statuses?: number[]
    type?: number[]
  } = {}
): Promise<ScenarioReportsWithSnapshots> => {
  const queryParams = new URLSearchParams()
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())
  if (params.begin !== undefined) queryParams.append('begin', params.begin.toString())
  if (params.end !== undefined) queryParams.append('end', params.end.toString())
  if (params.scenario !== undefined) queryParams.append('scenario', params.scenario.toString())
  if (params.file !== undefined) queryParams.append('file', params.file.toString())
  if (params.user !== undefined) queryParams.append('user', params.user.toString())
  params.statuses?.forEach(s => queryParams.append('statuses', s.toString()))
  params.type?.forEach(t => queryParams.append('type', t.toString()))
  const query = queryParams.toString() ? `?${queryParams.toString()}` : ''
  const pathWithQuery = `/workspaces/${workspaceId}/reports-with-snapshots${query}`
  return dedupeParallelGet(accessToken, pathWithQuery, () =>
    apiRequest(pathWithQuery, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
}

// --- Типы инструкций / процедур (совместимость с UI аналитики) ---

export interface Position {
  x: number
  y: number
  z: number
}

export interface Rotation {
  x: number
  y: number
  z: number
}

export interface Note {
  id: number
  aiId: number | null
  content: string
  position: Position
  instructionId: number
  created: string
  updated: string
  createdBy: number
  objectState: number
}

export interface CloudAnchorFallback {
  id: number
  imageContentId: number
  scale: number
  position: Position
  anchorPosition: Position
  anchorRotation: Rotation
}

export interface Marker {
  id: number
  type: number
  content: any
  assetId: number | null
  position: Position
  rotation: Rotation
  width: number
  objectState: number
  instructionId: number
}

export interface Area {
  id: number
  name: string
  description: string | null
  created: string
  updated: string
  previewImageId: number | null
  cloudAnchorId: number
  cloudAnchorFallback: CloudAnchorFallback
  workspaceId: number
  creatorId: number
  creator?: { id?: number; name?: string; login?: string } | null
  objectState: number
  markers: Marker[]
  /** Effective permissions bitmask for current user: 0=None, 1=Read, 2=Comment, 4=Edit, 8=Delete, 16=Execute */
  permissionValue?: number | null
  previewImage?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    workspaceId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

export interface FormQuestionImageSample {
  id: number
  index: number
  imageId: number
  isRight: boolean
  objectState: number
  formQuestionId: number
  image?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    groupId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

export interface FormQuestionAnswer {
  id: number
  formQuestionId: number
  index: number
  text: string
  imageId: number
  isRight: boolean
  objectState: number
}

export interface FormQuestion {
  id: number
  formId: number
  index: number
  type: 1 | 2 | 3 | 4 | 5 | 6
  data: string
  required: boolean
  hasRightAnswer: boolean
  rightAnswer: string | null
  showRightAnswer: boolean
  caseSensitive: boolean
  aiValidation: boolean
  aiPrompt: string | null
  wrongAnswerInstruction: string | null
  requireWrongAnswerDescription: boolean
  requireWrongAnswerPhoto: boolean
  answers: FormQuestionAnswer[]
  rightImageSamples: FormQuestionImageSample[]
  wrongImageSamples: FormQuestionImageSample[]
  objectState: number
}

export interface ScenarioForm {
  id: number
  scenarioStepId: number
  questions: FormQuestion[]
  objectState: number
}

export interface ScenarioStep {
  id: number
  name?: string
  description?: string | null
  detailedDescription?: string | null
  index?: number
  forms?: ScenarioForm[]
  attachments?: any[]
  requireAnswer?: boolean
  requirePhotoReport?: boolean
  position?: { x: number; y: number; z: number }
  objectState?: number
  /** ID этапа, к которому относится шаг (null — процедура без этапов / legacy) */
  moduleId?: number | null
  /** Незакрытые инциденты, привязанные к этому шагу (приходят вместе с процедурой) */
  incidents?: IncidentV3[] | null
}

/** Этап (группа шагов) процедуры — GET /scenarios/modules/{id} */
export interface ScenarioModule {
  id: number
  scenarioId?: number
  name: string
  description?: string | null
  index?: number
  /** Шаги этапа (могут дублировать Scenario.steps — источник правды всё равно плоский список) */
  steps?: ScenarioStep[]
  optimalCompletionTime?: number
  objectState?: number
  created?: string
  updated?: string
  version?: number
}

export enum ObjectStatusValue {
  Draft = 0,
  OnApproval = 1,
  Approved = 2,
  UnderRevision = 3,
  NotValid = 4,
}

export interface ObjectStatusDto {
  id: number
  value: ObjectStatusValue | null
  whoId?: number | null
  who?: { id: number; name: string; login: string } | null
  whomId?: number | null
  whom?: { id: number; name: string; login: string } | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  createdAt?: string
  updatedAt?: string
  version?: number
}

export interface SetStatusRequest {
  value: ObjectStatusValue | null
  whoId?: number | null
  whomId?: number | null
}

export interface ScenarioTypeV3 {
  id: number
  name: string
  workspaceId?: number | null
  creatorId?: number | null
  created?: string
  updated?: string
}

export interface Scenario {
  id: number
  name: string
  description?: string | null
  created: string
  updated: string
  createdBy: number
  creatorId?: number | null
  creator?: { name: string; login: string; id?: number; [key: string]: unknown } | null
  areaId?: number | null
  workspaceId?: number
  aiFileId?: number
  steps?: ScenarioStep[]
  /** Этапы (группы шагов). Пусто/отсутствует у процедур без этапов */
  modules?: ScenarioModule[] | null
  stepsCount?: number
  objectStatus?: ObjectStatusDto | null
  /** Effective permissions bitmask for current user: 0=None, 1=Read, 2=Comment, 4=Edit, 8=Delete, 16=Execute */
  permissionValue?: number | null
  scenarioTypeId?: number | null
  scenarioType?: ScenarioTypeV3 | null
  /** ID утверждающего пользователя */
  approverUserId?: number | null
  /** Утверждающий пользователь */
  approverUser?: { id?: number; name?: string; login?: string } | null
  /** ID утверждающей группы */
  approverGroupId?: number | null
  /** Утверждающая группа */
  approverGroup?: GroupV3 | null
  /** Линейное прохождение: шаги должны выполняться строго по порядку */
  sequentialExecution?: boolean | null
  /** Обязательные поля: нельзя отправить процедуру без заполнения всех обязательных полей */
  requireMandatoryFields?: boolean | null
}

export interface GetWorkspaceScenariosParams {
  /** Фильтр по ID участков */
  area?: number[]
  /** Фильтр по ID процедур */
  id?: number[]
  /** Фильтр по ID типов процедур */
  scenarioTypeId?: number[]
  /** Пропустить N элементов (пагинация) */
  skip?: number
  /** Взять N элементов, макс 1000 (пагинация) */
  take?: number
}

/** Все процедуры в workspace (GET /workspaces/{workspaceId}/scenarios) */
export const getWorkspaceScenarios = async (
  accessToken: string,
  workspaceId: number,
  params?: GetWorkspaceScenariosParams
): Promise<Scenario[]> => {
  const query = new URLSearchParams()
  if (params?.area?.length) params.area.forEach(v => query.append('area', String(v)))
  if (params?.id?.length) params.id.forEach(v => query.append('id', String(v)))
  if (params?.scenarioTypeId?.length) params.scenarioTypeId.forEach(v => query.append('scenarioTypeId', String(v)))
  if (params?.skip !== undefined) query.set('skip', String(params.skip))
  if (params?.take !== undefined) query.set('take', String(params.take))
  const qs = query.toString()
  const pathWithQuery = `/workspaces/${workspaceId}/scenarios${qs ? `?${qs}` : ''}`
  const result = await dedupeParallelGet(accessToken, pathWithQuery, () =>
    apiRequest(pathWithQuery, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
  // Авто-кэшируем справочник процедур для мгновенного отображения имён
  try { cacheScenarios(workspaceId, result) } catch {}
  return result
}

export interface GetAllScenariosParams {
  /** Фильтр по ID участков */
  areaId?: number[]
  /** Фильтр по ID процедур */
  id?: number[]
  /** Пропустить N элементов (пагинация) */
  skip?: number
  /** Взять N элементов, макс 1000 (пагинация) */
  take?: number
}

/** GET /scenarios/all — получить все процедуры с фильтрацией и пагинацией */
export const getAllScenarios = async (
  accessToken: string,
  params?: GetAllScenariosParams
): Promise<Scenario[]> => {
  const query = new URLSearchParams()
  if (params?.areaId?.length) {
    params.areaId.forEach(v => query.append('areaId', String(v)))
  }
  if (params?.id?.length) {
    params.id.forEach(v => query.append('id', String(v)))
  }
  if (params?.skip !== undefined) query.set('skip', String(params.skip))
  if (params?.take !== undefined) query.set('take', String(params.take))
  const qs = query.toString()
  const pathWithQuery = `/scenarios/all${qs ? `?${qs}` : ''}`
  return dedupeParallelGet(accessToken, pathWithQuery, () =>
    apiRequest(pathWithQuery, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
}

export interface AreaData {
  id: number
  notes: Note[]
  scenarios: Scenario[]
}

export interface Asset {
  id: number
  name: string
  type: string
  extension: string
  metadata: string | null
  shared: boolean
  createdAt: string
  updatedAt: string
  s3Id: string
  uploadedBy: number
  groupId: number | null
  url: string
}

export interface FormReportQuestion {
  questionId: number
  questionType: 1 | 2 | 3 | 4 | 5 | 6
  isSuccess?: boolean
  completedAt: number
  /** Время ответа на вопрос в секундах (мобильный формат, более приоритетно чем StartEnd) */
  totalSeconds?: number
  answer?: string
  rightAnswer?: string
  rightAnswers?: string[]
  answerId?: number
  answerContent?: string
  answerIds?: number[]
  answerContents?: string[]
  aiFeedback?: string
  aiResult?: boolean
  aiConfidence?: number
  photoId?: number
  photoIds?: number[]
  /** type 6 (подпись): ID ассета с PNG-изображением подписи */
  signatureId?: number
  /** type 6 (подпись): момент явного нажатия «Отправить» (epoch ms) — по аналогии с snapshotDate решений по инцидентам */
  signedAt?: number
  wrongAnswerDescription?: string
  wrongAnswerPhotoId?: number
  wrongAnswerAttachmentIds?: number[]
  /** Снапшоты инцидентов, подтверждённых пользователем по этому вопросу (legacy) */
  confirmedIncidents?: IncidentV3[]
  /**
   * Решения пользователя по инцидентам этого вопроса.
   * status: 1 = подтверждён, 0 = отклонён.
   */
  incidentDecisions?: Array<{
    incidentId: number
    status: 1 | 0
    snapshotDate?: number
    snapshot?: IncidentV3
  }>
}

export interface FormReport {
  formId: number
  questions: FormReportQuestion[]
}

export const getWorkspaceAreas = async (
  accessToken: string,
  workspaceId: number
): Promise<Area[]> => {
  const result: Area[] = await apiRequest(`/workspaces/${workspaceId}/areas`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
  // Авто-кэшируем справочник участков для мгновенного отображения имён
  try { cacheAreas(workspaceId, result) } catch {}
  return result
}

export const getArea = async (
  accessToken: string,
  areaId: number
): Promise<Area> => {
  const path = `/areas/${areaId}`
  const result: Area = await dedupeParallelGet(accessToken, path, () =>
    apiRequest(path, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
  )
  // Кэшируем имя участка, чтобы в интерфейсе не мелькали числовые ID
  try { if (result?.workspaceId && result.name) cacheAreas(result.workspaceId, [result]) } catch {}
  return result
}

export const getAreaData = async (
  accessToken: string,
  areaId: number
): Promise<AreaData> => {
  return apiRequest(`/areas/${areaId}/data`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export const getScenario = async (
  accessToken: string | null | undefined,
  scenarioId: number
): Promise<Scenario> => {
  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  const scenario: Scenario = await apiRequest(`/scenarios/${scenarioId}`, {
    method: 'GET',
    headers,
  })
  // Бэкенд отдаёт шаги этапов только внутри modules[].steps, а в плоском steps — лишь шаги без этапа.
  // Собираем единый плоский список (порядок: этапы → index внутри этапа), чтобы все потребители
  // scenario.steps продолжали работать без изменений.
  return withOrderedSteps(scenario, { includeDeleted: true })
}

export const getAsset = async (
  accessToken: string | null | undefined,
  id: number,
  signal?: AbortSignal
): Promise<Asset> => {
  const headers: Record<string, string> = {}
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  return apiRequest(`/assets/${id}`, {
    method: 'GET',
    headers,
    signal,
  })
}

// ===============================
// 📁 AI Файлы (API v3)
// ===============================

export interface FileAssetV3 {
  id: number
  name?: string
  type?: string
  extension?: string
  metadata?: string | null
  isPublic?: boolean
  s3Id?: string
  workspaceId?: number | null
  fileSize?: number
  url?: string | null
  staticUrl?: string | null
  creatorId?: number | null
  creator?: UserV3 | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface AiFileV3 {
  id: number
  fileId?: string
  name?: string
  extension?: string
  workspaceId?: number
  assetId?: number
  asset?: FileAssetV3
  isHidden?: boolean
  maxTokensPerParagraph?: number
  overlappingTokens?: number
  creatorId?: number | null
  creator?: UserV3 | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

/** 0 — идёт, 1 — готов, 2 — ошибка, 3 — повтор после ошибки. Готовность файла определяется этим полем, не aiFileId. */
export const DocumentImportState = {
  InProgress: 0,
  Ready: 1,
  Error: 2,
  Retrying: 3,
} as const

export interface DocumentImportStatus {
  status: number
  errorText?: string | null
  /** Прогресс от 0 до 1 */
  progress: number
}

export interface FileEntityV3 {
  id: number
  aiFileId?: number
  aiFile?: AiFileV3 | null
  documentImportStatus?: DocumentImportStatus | null
  name: string
  isPublic?: boolean
  assetId?: number
  asset?: FileAssetV3
  workspaceId?: number
  creatorId?: number | null
  creator?: UserV3 | null
  created: string
  updated: string
  objectState?: number
  objectStatus?: ObjectStatusDto | null
  /** Права текущего пользователя: 1 чтение, 2 комментарии, 4 правка, 8 удаление, 16 запуск */
  permissionValue?: number | null
  folderId?: number | null
  version?: number
  /** Данные автора — берётся из file.creator или file.asset.creator */
  resolvedCreator?: UserV3 | null
}

export interface GetAiFilesParams {
  workspace: number
  sort?: number
  skip?: number
  take?: number
}

interface FilesPaginatorResult {
  totalCount: number
  result: FileEntityV3[] | null
}

/**
 * 📁 Получить список файлов.
 * Данные автора берутся из поля creator, агрегированного сервером.
 */
export async function getAiFiles(
  accessToken: string,
  params: GetAiFilesParams
): Promise<FileEntityV3[]> {
  const queryParams = new URLSearchParams()
  queryParams.append('workspace', params.workspace.toString())
  if (params.sort !== undefined) queryParams.append('sort', params.sort.toString())
  if (params.skip !== undefined) queryParams.append('skip', params.skip.toString())
  if (params.take !== undefined) queryParams.append('take', params.take.toString())

  const data: FilesPaginatorResult = await apiRequest(
    `/files?${queryParams.toString()}`,
    {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  )

  const files: FileEntityV3[] = data.result ?? []

  // creator уже агрегирован сервером — дополнительных запросов не нужно
  return files.map(file => ({
    ...file,
    resolvedCreator: file.creator ?? file.asset?.creator ?? null,
  }))
}

/**
 * 📄 Получить файл по числовому id.
 * Возвращает null если файл не найден (404/403) или был удалён.
 */
export async function getFileById(
  accessToken: string,
  fileId: number
): Promise<FileEntityV3 | null> {
  try {
    const data: FileEntityV3 = await apiRequest(`/files/${fileId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    return {
      ...data,
      resolvedCreator: data.creator ?? data.asset?.creator ?? null,
    }
  } catch {
    return null
  }
}

/** POST /files/{fileId}/status/update — статус файла (черновик, на согласовании, утверждён…) */
export async function updateFileStatus(
  accessToken: string,
  fileId: number,
  data: SetStatusRequest
): Promise<FileEntityV3> {
  return apiRequest(`/files/${fileId}/status/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * 🗑️ Удалить файл по числовому id.
 */
export async function deleteAiFile(
  accessToken: string,
  fileId: number
): Promise<{ success: boolean; error?: string }> {
  debugGroup('api', `🗑️ FILE DELETE START: id=${fileId}`)
  try {
    await apiRequest(`/files/${fileId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    })
    debugLog('api', '✅ Delete SUCCESS!')
    debugGroupEnd('api')
    return { success: true }
  } catch (error) {
    debugError('api', '❌ Delete FAILED:', error)
    debugGroupEnd('api')
    const errorMessage = error instanceof Error ? error.message : 'Неизвестная ошибка при удалении файла'
    return { success: false, error: errorMessage }
  }
}

/**
 * 🔄 Повторно проиндексировать файл (reimport).
 * Нужен, когда documentImportStatus.status === 2 (ошибка индексации).
 */
export const reimportFile = async (
  accessToken: string,
  fileId: number
): Promise<void> => {
  return apiRequest(`/files/${fileId}/reimport`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/**
 * 📤 Загрузить файл.
 * @param workspaceId - ID рабочего пространства (WorkspaceId в FormData)
 */
export const NAME_CONFLICT_DECISION = {
  CreateCopy: 0,
  Update: 1,
} as const

export type NameConflictDecision = typeof NAME_CONFLICT_DECISION[keyof typeof NAME_CONFLICT_DECISION]

export async function uploadAiFile(
  accessToken: string,
  file: File,
  workspaceId: number,
  name: string,
  onProgress?: (progress: number) => void,
  folderId?: number | null,
  nameConflictDecision?: NameConflictDecision
): Promise<{ success: boolean; fileId?: string; error?: string; conflict?: boolean }> {
  const fileSize = (file.size / 1024 / 1024).toFixed(2)
  // Напрямую в V3, не через функцию Next.js: тело запроса к функции на Vercel ограничено 4,5 МБ
  const requestUrl = `${API_BASE_URL}/files/create`

  const fields: Record<string, string | number> = {
    Name: name,
    WorkspaceId: workspaceId,
    ...(folderId != null ? { FolderId: folderId } : {}),
    ...(nameConflictDecision !== undefined ? { NameConflictDecision: nameConflictDecision } : {}),
  }

  debugGroup('api', `📤 [uploadAiFile] POST ${requestUrl}`)
  debugLog('api', '  File:', file.name, `(${fileSize} MB)`)
  debugLog('api', '  Fields:', fields)

  return new Promise((resolve) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        if (onProgress) onProgress(percentComplete)
      }
    })

    xhr.addEventListener('load', () => {
      debugLog('api', `  ← ${xhr.status} ${xhr.statusText}`)
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          debugLog('api', '  ✅ fileId:', result.id)
          debugGroupEnd('api')
          resolve({ success: true, fileId: String(result.id) })
        } catch {
          debugGroupEnd('api')
          resolve({ success: false, error: 'Ошибка парсинга ответа сервера' })
        }
      } else {
        let errorMessage = `HTTP ${xhr.status}: ${xhr.statusText}`
        const isConflict = xhr.status === 409
        try {
          const errorData = JSON.parse(xhr.responseText)
          debugLog('api', '  ❌ Response body:', errorData)
          if (errorData.details?.message) {
            errorMessage = errorData.details.message
          } else if (errorData.details?.raw) {
            errorMessage = errorData.details.raw
          } else if (errorData.message) {
            errorMessage = errorData.message
          } else if (errorData.detail || errorData.title) {
            errorMessage = errorData.detail || errorData.title
          } else if (errorData.error) {
            errorMessage = errorData.error
          }
        } catch {
          debugLog('api', '  ❌ Raw response:', xhr.responseText.slice(0, 200))
        }
        if (isConflict) debugLog('api', '  ⚠️ Конфликт имени файла (409)')
        debugGroupEnd('api')
        resolve({ success: false, error: errorMessage, conflict: isConflict })
      }
    })

    xhr.addEventListener('error', () => {
      debugError('Сетевая ошибка при загрузке файла')
      debugGroupEnd('api')
      resolve({ success: false, error: 'Ошибка сетевого соединения' })
    })

    xhr.addEventListener('abort', () => {
      debugGroupEnd('api')
      resolve({ success: false, error: 'Загрузка отменена' })
    })

    const formData = new FormData()
    formData.append('File', file)
    formData.append('Name', name)
    formData.append('WorkspaceId', workspaceId.toString())
    if (folderId != null) formData.append('FolderId', folderId.toString())
    if (nameConflictDecision !== undefined) formData.append('NameConflictDecision', nameConflictDecision.toString())

    xhr.open('POST', requestUrl, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    // Content-Type не устанавливаем — браузер сам проставляет multipart/form-data с boundary
    xhr.send(formData)
  })
}

// ============================================================
// 📦 АССЕТЫ И ФАЙЛЫ
// ============================================================

export interface Asset {
  id: number
  name: string
  type: string
  extension: string
  metadata: string | null
  shared: boolean
  createdAt: string
  updatedAt: string
  s3Id: string
  uploadedBy: number
  groupId: number | null
  url: string
}

export interface UploadAssetParams {
  name: string
  type: string
  workspaceId: number
  file: File
  metadata?: any
  shared?: boolean
  /** IsPublic: ссылку на файл (GET /assets/{id}) получит любой пользователь Casper, не только участники пространства */
  isPublic?: boolean
  onProgress?: (progress: number) => void
  signal?: AbortSignal
}

export const uploadAsset = async (
  accessToken: string,
  params: UploadAssetParams
): Promise<Asset> => {
  const url = `${API_BASE_URL}/assets/upload`

  debugLog('api', `📤 Uploading asset: ${params.file.name}`)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        const percentComplete = Math.round((event.loaded / event.total) * 100)
        if (params.onProgress) {
          params.onProgress(percentComplete)
        }
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const result = JSON.parse(xhr.responseText)
          debugLog('api', `✅ Asset uploaded: ID ${result.id}`)
          resolve(result)
        } catch (error) {
          reject(new Error('Ошибка парсинга ответа сервера'))
        }
      } else {
        reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => {
      reject(new Error('Ошибка сетевого соединения'))
    })

    xhr.addEventListener('abort', () => {
      reject(new Error('Загрузка отменена'))
    })

    if (params.signal) {
      params.signal.addEventListener('abort', () => {
        xhr.abort()
      })
    }

    const formData = new FormData()
    formData.append('Name', params.name)
    formData.append('Type', params.type)
    formData.append('WorkspaceId', params.workspaceId.toString())
    formData.append('File', params.file)
    if (params.metadata) formData.append('Metadata', JSON.stringify(params.metadata))
    if (params.shared !== undefined) formData.append('Shared', String(params.shared))
    if (params.isPublic !== undefined) formData.append('IsPublic', String(params.isPublic))

    xhr.open('POST', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.send(formData)
  })
}

// ============================================================
// 🤖 AI-ВАЛИДАЦИЯ
// ============================================================

export interface PhotoValidationRequest {
  questionId: number
  photoAssetId: number
}

export interface PhotoValidationResult {
  result: boolean
  confidence: number
  feedback: string
}

export const validatePhoto = async (
  accessToken: string,
  request: PhotoValidationRequest
): Promise<PhotoValidationResult> => {
  debugLog('api', `🤖 Validating photo: Q${request.questionId}, Asset${request.photoAssetId}`)
  return apiRequest('/ai/photo/form/question', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
}

// ============================================================
// 📝 ИНСТРУКЦИИ — МУТАЦИИ
// ============================================================

export interface AreaCreateRequest {
  name: string
  description?: string | null
  workspaceId: number
  cloudAnchorId: number
  previewImageId?: number | null
  folderId?: number | null
}

export interface AreaUpdateRequest {
  name?: string | null
  description?: string | null
  cloudAnchorId?: number | null
  previewImageId?: number | null
  /** Папка, в которой участок является entity (null не поддерживается сервером — 400) */
  folderId?: number | null
}

export const createArea = async (
  accessToken: string,
  data: AreaCreateRequest
): Promise<Area> => {
  const result: Area = await apiRequest('/areas/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  // Сразу кладём имя нового участка в кэш названий
  try { if (result?.id && result.name) cacheAreas(result.workspaceId ?? data.workspaceId, [result]) } catch {}
  return result
}

export const deleteArea = async (
  accessToken: string,
  areaId: number
): Promise<void> => {
  return apiRequest(`/areas/${areaId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateArea = async (
  accessToken: string,
  areaId: number,
  data: AreaUpdateRequest
): Promise<Area> => {
  const result: Area = await apiRequest(`/areas/${areaId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
  // Обновляем имя участка в кэше названий
  try { if (result?.workspaceId && result.name) cacheAreas(result.workspaceId, [result]) } catch {}
  return result
}

// ============================================================
// 🎬 УПРАВЛЕНИЕ ПРОЦЕДУРАМИ
// ============================================================

export interface ScenarioCreateRequest {
  name: string
  description?: string | null
  areaId?: number | null
  /** Папка Библиотеки, в которую помещается процедура (не влияет на areaId) */
  folderId?: number | null
  workspaceId: number
  scenarioTypeId?: number | null
  approverUserId?: number | null
  approverGroupId?: number | null
}

export interface ScenarioUpdateRequest {
  name?: string | null
  description?: string | null
  scenarioTypeId?: number | null
  areaId?: number | null
  /** Папка Библиотеки, в которую перемещается процедура */
  folderId?: number | null
  /** null или <= 0 сбрасывают утверждающего пользователя */
  approverUserId?: number | null
  /** null или <= 0 сбрасывают утверждающую группу */
  approverGroupId?: number | null
  /** Линейное прохождение */
  sequentialExecution?: boolean | null
  /** Обязательные поля для отправки */
  requireMandatoryFields?: boolean | null
}

export const moveScenarioToArea = async (
  accessToken: string,
  scenarioId: number,
  areaId: number
): Promise<Scenario> => {
  return apiRequest(`/scenarios/${scenarioId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ areaId }),
  })
}

export const createScenario = async (
  accessToken: string,
  data: ScenarioCreateRequest
): Promise<Scenario> => {
  return apiRequest(`/scenarios/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateScenario = async (
  accessToken: string,
  scenarioId: number,
  data: ScenarioUpdateRequest
): Promise<Scenario> => {
  return apiRequest(`/scenarios/${scenarioId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteScenario = async (
  accessToken: string,
  scenarioId: number
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const copyScenario = async (
  accessToken: string,
  scenarioId: number,
  areaId?: number
): Promise<Scenario> => {
  const params = areaId !== undefined ? `?area=${areaId}` : ''
  return apiRequest(`/scenarios/${scenarioId}/copy${params}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateScenarioStatus = async (
  accessToken: string,
  scenarioId: number,
  data: SetStatusRequest
): Promise<ObjectStatusDto> => {
  return apiRequest(`/scenarios/${scenarioId}/status/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** GET /scenarios/{scenarioId}/ai/content — получить AI-контент процедуры в виде текста */
export const getScenarioAiContent = async (
  accessToken: string,
  scenarioId: number
): Promise<string> => {
  return apiRequest(`/scenarios/${scenarioId}/ai/content`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ============================================================
// 📊 ОТЧЁТ ПО ПРОЦЕДУРЕ
// ============================================================

export interface TimelineSession {
  stepId: number
  startTime: number
  endTime: number
  marked?: boolean
  photoId?: number
  answer?: string
  formReport?: {
    formId: number
    questions: Array<{
      questionId: number
      questionType: 1 | 2 | 3 | 4 | 5 | 6
      isSuccess?: boolean
      completedAt: number
      answer?: string
      rightAnswer?: string
      rightAnswers?: string[]
      answerId?: number
      answerContent?: string
      aiFeedback?: string
      aiResult?: boolean
      aiConfidence?: number
      photoId?: number
      photoIds?: number[]
      signatureId?: number
      signedAt?: number
      wrongAnswerDescription?: string
      wrongAnswerPhotoId?: number
      wrongAnswerAttachmentIds?: number[]
    }>
  }
}

export interface ScenarioReportData {
  newFormat?: boolean
  platform?: 'desktop' | 'mobile'
  /**
   * questionId → incidentId → решение пользователя.
   * "confirmed" — инцидент подтверждён и попадает в отчёт снапшотом,
   * "rejected" — отклонён, "pending" — ожидает решения.
   * Поддерживается legacy плоский формат v1 (questionId → decision) при чтении.
   */
  incidentDecisions?: Record<string, Record<string, "confirmed" | "rejected" | "pending">>
  steps: Array<{
    stepId: number
    endTime: number
    photoId?: number
    answer?: string
    /** Решения пользователя по инцидентам уровня шага (не привязанным к конкретному вопросу). */
    incidentDecisions?: Array<{
      incidentId: number
      status: 1 | 0
      snapshotDate?: number
      snapshot?: IncidentV3
    }>
    formReport?: {
      formId: number
      questions: Array<{
        questionId: number
        questionType: 1 | 2 | 3 | 4 | 5 | 6
        isSuccess?: boolean
        completedAt: number
        answer?: string
        rightAnswer?: string
        rightAnswers?: string[]
        answerId?: number
        answerContent?: string
        aiFeedback?: string
        aiResult?: boolean
        aiConfidence?: number
        photoId?: number
        photoIds?: number[]
        signatureId?: number
        signedAt?: number
        wrongAnswerDescription?: string
        wrongAnswerPhotoId?: number
        wrongAnswerAttachmentIds?: number[]
        confirmedIncidents?: IncidentV3[]
        incidentDecisions?: Array<{
          incidentId: number
          status: 1 | 0
          snapshotDate?: number
          snapshot?: IncidentV3
        }>
      }>
    }
  }>
  timeline?: TimelineSession[]
}

export interface SubmitScenarioReportParams {
  scenarioId: number
  start: number
  end: number
  timezone: number
  steps: number
  total: number
  completed: boolean
  reportData: ScenarioReportData
  /** ID назначения — привязывается к отчёту при завершении (legacy одноразовый create) */
  assignmentId?: number
}

export const submitScenarioReport = async (
  accessToken: string,
  params: SubmitScenarioReportParams
): Promise<any> => {
  const queryParams = new URLSearchParams()
  queryParams.append('start', params.start.toString())
  queryParams.append('end', params.end.toString())
  queryParams.append('timezone', params.timezone.toString())
  queryParams.append('steps', params.steps.toString())
  queryParams.append('total', params.total.toString())
  queryParams.append('completed', String(params.completed))
  if (params.assignmentId !== undefined) queryParams.append('assignmentId', String(params.assignmentId))

  const url = `/instructions/scenarios/${params.scenarioId}/reports/create?${queryParams.toString()}`

  debugLog('api', `📊 Submitting report: Scenario ${params.scenarioId}, ${params.steps}/${params.total} steps`)

  return apiRequest(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params.reportData),
  })
}

export interface CreateScenarioReportDirectParams {
  scenarioId: number
  start: number
  end?: number
  timezone: number
  area?: number
  steps?: number
  total?: number
  /** По умолчанию true на backend, если не передать — явно указываем */
  completed?: boolean
  /**
   * Согласно OpenAPI-описанию параметра ("Scenario assignment ID to link when report is
   * completed") привязка к assignment.scenarioReportId должна происходить только когда
   * completed=true. Функция существует для эмпирической проверки — работает ли это и при
   * completed=false (см. /test-api-v3 → блок "Scenario Assignment").
   */
  assignmentId?: number
  reportData: ScenarioReportData
}

/**
 * POST /api/v3/scenarios/{scenarioId}/reports/create — ОТДЕЛЬНЫЙ от /instructions/... и от
 * real-time (start→update→complete) одноразовый эндпоинт создания отчёта целиком (полный
 * JSON сразу в теле). Как и submitScenarioReport, принимает assignmentId, но по документации
 * это поле линкуется с assignment ТОЛЬКО в момент завершения (completed=true).
 */
export const createScenarioReportDirect = async (
  accessToken: string,
  params: CreateScenarioReportDirectParams
): Promise<any> => {
  const qs = new URLSearchParams()
  qs.set('start', String(params.start))
  if (params.end !== undefined) qs.set('end', String(params.end))
  qs.set('timezone', String(params.timezone))
  if (params.area !== undefined) qs.set('area', String(params.area))
  if (params.steps !== undefined) qs.set('steps', String(params.steps))
  if (params.total !== undefined) qs.set('total', String(params.total))
  if (params.completed !== undefined) qs.set('completed', String(params.completed))
  if (params.assignmentId !== undefined) qs.set('assignmentId', String(params.assignmentId))

  return apiRequest(`/scenarios/${params.scenarioId}/reports/create?${qs.toString()}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params.reportData),
  })
}

// ============================================================
// 🔴 REAL-TIME ОТЧЁТ (start → update → complete)
// ============================================================

/**
 * Статус real-time отчёта (ScenarioReportStatus в OpenAPI).
 * 0 - Created; 1 - In progress; 2 - Paused; 3 - Sent (отчёт отправлен/завершён).
 */
export enum ScenarioReportStatus {
  Created = 0,
  InProgress = 1,
  Paused = 2,
  Sent = 3,
}

/** Ответ /reports/start, /reports/{id}/update, /reports/{id}/complete (схема ScenarioReport в OpenAPI). */
export interface RealtimeScenarioReport {
  id: number
  scenarioId: number
  areaId?: number | null
  scenarioSnapshotId?: number | null
  /** Привязанное назначение процедуры, если есть (заполняется только при complete с assignmentId) */
  scenarioAssignmentId?: number | null
  dataId?: number
  status?: ScenarioReportStatus
  startTime?: number
  endTime?: number
  timeZone?: number
  completedSteps?: number | null
  totalSteps?: number | null
  isCompleted?: boolean | null
  totalTime?: number | null
  /** Пользователь, который проходит/прошёл процедуру (создатель отчёта) */
  creatorId?: number | null
  /** Платформа, с которой запущено прохождение ('mobile', 'desktop' или null). */
  platform?: string | null
}

export interface StartScenarioReportParams {
  scenarioId: number
  /** Start time in UTC Unix time */
  start: number
  /** User time zone */
  timezone: number
  /** Если процедура проходится в AR */
  area?: number
  /** Общее количество шагов процедуры */
  total?: number
}

/**
 * POST /api/v3/scenarios/{scenarioId}/reports/start — создать real-time отчёт.
 * Вызывается один раз, как только пользователь запускает прохождение процедуры.
 */
export const startScenarioReport = async (
  accessToken: string,
  params: StartScenarioReportParams
): Promise<RealtimeScenarioReport> => {
  const qs = new URLSearchParams()
  qs.set('start', String(params.start))
  qs.set('timezone', String(params.timezone))
  if (params.area !== undefined) qs.set('area', String(params.area))
  if (params.total !== undefined) qs.set('total', String(params.total))
  return apiRequest(`/scenarios/${params.scenarioId}/reports/start?${qs.toString()}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export interface UpdateScenarioReportParams {
  scenarioId: number
  reportId: number
  /** Статус после обновления: 1 - In progress; 2 - Paused */
  status?: ScenarioReportStatus
  end?: number
  /** Число завершённых шагов */
  steps?: number
  /** Общее количество шагов */
  total?: number
  /** Завершена ли процедура */
  completed?: boolean
  /** ID назначения — привязывается к отчёту при обновлении (аналогично complete) */
  assignmentId?: number
  /** Полное актуальное JSON-тело отчёта (то же, что уходит в reports/create) */
  reportData?: ScenarioReportData
  /**
   * Последнее УСПЕШНО отправленное тело отчёта (то, что уже точно лежит на сервере).
   * Используется для построения МИНИМАЛЬНОГО JSON Patch — если не передано (например,
   * это первый update после start), отправляется patch создания всех полей целиком.
   */
  previousReportData?: ScenarioReportData | null
  /** Отправить запрос с флагом keepalive (переживает выгрузку страницы) — для beforeunload */
  keepalive?: boolean
}

interface JsonPatchOp {
  op: 'add' | 'replace' | 'remove'
  path: string
  value?: unknown
}

/** Точечный diff одного массива (steps/timeline) в операции JSON Patch по индексам. */
function diffReportArrayField(field: string, prevArr: unknown[], nextArr: unknown[]): JsonPatchOp[] {
  const ops: JsonPatchOp[] = []
  const minLen = Math.min(prevArr.length, nextArr.length)
  for (let i = 0; i < minLen; i++) {
    if (JSON.stringify(prevArr[i]) !== JSON.stringify(nextArr[i])) {
      ops.push({ op: 'replace', path: `/${field}/${i}`, value: nextArr[i] })
    }
  }
  // Новые элементы — добавляем по одному в конец ("-" — индекс "после последнего" по RFC 6902)
  for (let i = minLen; i < nextArr.length; i++) {
    ops.push({ op: 'add', path: `/${field}/-`, value: nextArr[i] })
  }
  // Если массив стал короче — удаляем хвост (с конца, чтобы индексы не сдвигались)
  for (let i = prevArr.length - 1; i >= nextArr.length; i--) {
    ops.push({ op: 'remove', path: `/${field}/${i}` })
  }
  return ops
}

/**
 * Строит МИНИМАЛЬНЫЙ JSON Patch (RFC 6902) между предыдущим (уже сохранённым на сервере)
 * и текущим содержимым отчёта: скалярные поля (`newFormat`/`platform`) патчатся только
 * при изменении, а массивы (`steps`/`timeline`) — точечно по индексам ("replace" для
 * изменившихся элементов, "add" в конец для новых, "remove" для удалённых), а не
 * пересылкой всего документа/массива целиком при каждом шаге.
 */
export function buildScenarioReportPatch(
  prev: ScenarioReportData | null | undefined,
  next: ScenarioReportData
): JsonPatchOp[] {
  if (!prev) {
    // Ничего ещё не отправлено — создаём все поля документа
    return Object.entries(next).map(([key, value]) => ({ op: 'add', path: `/${key}`, value }))
  }
  const ops: JsonPatchOp[] = []
  if (prev.newFormat !== next.newFormat) ops.push({ op: 'add', path: '/newFormat', value: next.newFormat })
  if (prev.platform !== next.platform) ops.push({ op: 'add', path: '/platform', value: next.platform })
  ops.push(...diffReportArrayField('steps', prev.steps ?? [], next.steps ?? []))
  ops.push(...diffReportArrayField('timeline', prev.timeline ?? [], next.timeline ?? []))
  if (JSON.stringify(prev.incidentDecisions ?? null) !== JSON.stringify(next.incidentDecisions ?? null)) {
    if (next.incidentDecisions !== undefined) {
      ops.push({ op: 'add', path: '/incidentDecisions', value: next.incidentDecisions })
    } else {
      ops.push({ op: 'remove', path: '/incidentDecisions' })
    }
  }
  return ops
}

/**
 * PATCH /api/v3/scenarios/{scenarioId}/reports/{id}/update — обновить real-time отчёт.
 *
 * API ожидает тело в формате JSON Patch (RFC 6902). Отправляем МИНИМАЛЬНЫЙ патч —
 * только то, что изменилось с прошлой успешной отправки (см. buildScenarioReportPatch),
 * а не весь документ целиком. Если `previousReportData` не передан — считаем, что это
 * первая отправка содержимого для этого отчёта, и создаём все поля через "add".
 */
export const updateScenarioReport = async (
  accessToken: string,
  params: UpdateScenarioReportParams
): Promise<RealtimeScenarioReport> => {
  const qs = new URLSearchParams()
  if (params.status !== undefined) qs.set('status', String(params.status))
  if (params.end !== undefined) qs.set('end', String(params.end))
  if (params.steps !== undefined) qs.set('steps', String(params.steps))
  if (params.total !== undefined) qs.set('total', String(params.total))
  if (params.completed !== undefined) qs.set('completed', String(params.completed))
  if (params.assignmentId !== undefined) qs.set('assignmentId', String(params.assignmentId))
  const query = qs.toString() ? `?${qs.toString()}` : ''

  const patchBody = params.reportData !== undefined
    ? buildScenarioReportPatch(params.previousReportData, params.reportData)
    : []

  return apiRequest(`/scenarios/${params.scenarioId}/reports/${params.reportId}/update${query}`, {
    method: 'PATCH',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(patchBody),
    ...(params.keepalive ? { keepalive: true } : {}),
  })
}

export interface CompleteScenarioReportParams {
  scenarioId: number
  reportId: number
  end?: number
  steps?: number
  total?: number
  completed?: boolean
  /** ID назначения — привязывается к отчёту при завершении */
  assignmentId?: number
}

/**
 * POST /api/v3/scenarios/{scenarioId}/reports/{id}/complete — завершить real-time отчёт.
 * Переводит отчёт в статус "Sent" (отправлен/завершён) и, если передан assignmentId,
 * связывает его с назначением процедуры.
 */
export const completeScenarioReport = async (
  accessToken: string,
  params: CompleteScenarioReportParams
): Promise<RealtimeScenarioReport> => {
  const qs = new URLSearchParams()
  if (params.end !== undefined) qs.set('end', String(params.end))
  if (params.steps !== undefined) qs.set('steps', String(params.steps))
  if (params.total !== undefined) qs.set('total', String(params.total))
  if (params.completed !== undefined) qs.set('completed', String(params.completed))
  if (params.assignmentId !== undefined) qs.set('assignmentId', String(params.assignmentId))
  const query = qs.toString() ? `?${qs.toString()}` : ''

  return apiRequest(`/scenarios/${params.scenarioId}/reports/${params.reportId}/complete${query}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * DELETE /api/v3/scenarios/{scenarioId}/reports/{id} — удалить отчёт.
 * Используется для очистки «черновика» real-time отчёта после того, как финальное
 * (гарантированно корректное) содержимое сохранено через submitScenarioReport (create).
 */
export const deleteScenarioReport = async (
  accessToken: string,
  scenarioId: number,
  reportId: number
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/reports/${reportId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * GET /api/v3/scenarios/{scenarioId}/reports/{id} — получить один real-time отчёт по ID.
 */
export const getScenarioReport = async (
  accessToken: string,
  scenarioId: number,
  reportId: number,
  recalc?: boolean
): Promise<RealtimeScenarioReport> => {
  const qs = recalc !== undefined ? `?recalc=${String(recalc)}` : ''
  return apiRequest(`/scenarios/${scenarioId}/reports/${reportId}${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * GET /api/v3/scenarios/{scenarioId}/reports — список real-time отчётов процедуры
 * (используется, чтобы найти незавершённый отчёт текущего пользователя для «продолжить»).
 */
export const getScenarioReportsList = async (
  accessToken: string,
  scenarioId: number,
  params: { skip?: number; take?: number; statuses?: ScenarioReportStatus[] } = {}
): Promise<RealtimeScenarioReport[]> => {
  const qs = new URLSearchParams()
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  if (params.take !== undefined) qs.set('take', String(params.take))
  if (params.statuses !== undefined) {
    for (const s of params.statuses) qs.append('statuses', String(s))
  }
  const query = qs.toString() ? `?${qs.toString()}` : ''
  return apiRequest(`/scenarios/${scenarioId}/reports${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * Загружает и парсит полное JSON-содержимое (steps/timeline) real-time отчёта по его dataId.
 * Возвращает null, если содержимого ещё нет или его не удалось получить/распарсить.
 */
export const fetchScenarioReportData = async (
  accessToken: string,
  report: RealtimeScenarioReport
): Promise<ScenarioReportData | null> => {
  if (!report.dataId) return null
  try {
    const asset = await getAsset(accessToken, report.dataId)
    if (!asset?.url) return null
    const res = await fetch(asset.url)
    if (!res.ok) return null
    const text = await res.text()
    if (!text) return null
    return JSON.parse(text) as ScenarioReportData
  } catch {
    return null
  }
}

// ============================================================
// 📄 FILE REPORTS — отчёты о прохождении процедур-файлов (.cspr)
// ============================================================

/**
 * Отчёт о прохождении процедуры-файла (`.cspr` в Библиотеке). Версия процедуры фиксируется при
 * старте коммитом файла (`fileCommitId`) и больше не меняется. Тело отчёта — тот же
 * `ScenarioReportData` (произвольные поля сохраняются), лежит в ассете `dataId`.
 */
export interface FileReport {
  id: number
  fileId: number
  fileCommitId: number
  dataId?: number | null
  areaId?: number | null
  status?: ScenarioReportStatus
  startTime?: number
  endTime?: number | null
  timeZone?: number
  completedSteps?: number | null
  totalSteps?: number | null
  isCompleted?: boolean | null
  totalTime?: number | null
  creatorId?: number | null
  creator?: { name?: string | null; login?: string | null } | null
  platform?: string | null
}

const fileReportQuery = (params: Record<string, string | number | boolean | undefined | null>): string => {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null) qs.set(key, String(value))
  const query = qs.toString()
  return query ? `?${query}` : ''
}

export interface StartFileReportParams {
  fileId: number
  /** Коммит файла, по которому идёт прохождение; по умолчанию — активный коммит */
  commit?: number | null
  start: number
  timezone: number
  area?: number
  total?: number
  reportData?: ScenarioReportData
}

/** POST /api/v3/files/{fileId}/reports/start — начать прохождение и зафиксировать версию файла. */
export const startFileReport = async (accessToken: string, params: StartFileReportParams): Promise<FileReport> => {
  const query = fileReportQuery({ commit: params.commit, start: params.start, timezone: params.timezone, area: params.area, total: params.total })
  return apiRequest(`/files/${params.fileId}/reports/start${query}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    ...(params.reportData !== undefined ? { body: JSON.stringify(params.reportData) } : {}),
  })
}

export interface CreateFileReportParams {
  fileId: number
  commit?: number | null
  start: number
  end: number
  timezone: number
  area?: number
  steps: number
  total: number
  completed: boolean
  reportData: ScenarioReportData
}

/** POST /api/v3/files/{fileId}/reports/create — завершённый отчёт целиком одним запросом. */
export const createFileReport = async (accessToken: string, params: CreateFileReportParams): Promise<FileReport> => {
  const query = fileReportQuery({
    start: params.start,
    end: params.end,
    commit: params.commit,
    timezone: params.timezone,
    area: params.area,
    steps: params.steps,
    total: params.total,
    completed: params.completed,
  })
  return apiRequest(`/files/${params.fileId}/reports/create${query}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(params.reportData),
  })
}

export interface UpdateFileReportParams {
  fileId: number
  reportId: number
  status?: ScenarioReportStatus
  end?: number
  steps?: number
  total?: number
  completed?: boolean
  /** Длительность прохождения без пауз, мс */
  totalTime?: number
  reportData?: ScenarioReportData
  /** Последнее отправленное тело — патч строится как разница с ним (см. buildScenarioReportPatch) */
  previousReportData?: ScenarioReportData | null
  /** Готовый JSON Patch вместо разницы `reportData` */
  patch?: JsonPatchOp[]
  keepalive?: boolean
}

/** PATCH /api/v3/files/{fileId}/reports/{id}/update — JSON Patch тела отчёта; версия файла не меняется. */
export const updateFileReport = async (accessToken: string, params: UpdateFileReportParams): Promise<FileReport> => {
  const query = fileReportQuery({
    status: params.status,
    end: params.end,
    steps: params.steps,
    total: params.total,
    completed: params.completed,
    totalTime: params.totalTime,
  })
  const patch = params.patch ?? (params.reportData !== undefined ? buildScenarioReportPatch(params.previousReportData, params.reportData) : [])
  return apiRequest(`/files/${params.fileId}/reports/${params.reportId}/update${query}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
    ...(params.keepalive ? { keepalive: true } : {}),
  })
}

export interface CompleteFileReportParams {
  fileId: number
  reportId: number
  end?: number
  steps?: number
  total?: number
  completed?: boolean
  totalTime?: number
}

/** POST /api/v3/files/{fileId}/reports/{id}/complete — завершить прохождение (версия файла не меняется). */
export const completeFileReport = async (accessToken: string, params: CompleteFileReportParams): Promise<FileReport> => {
  const query = fileReportQuery({ end: params.end, steps: params.steps, total: params.total, completed: params.completed, totalTime: params.totalTime })
  return apiRequest(`/files/${params.fileId}/reports/${params.reportId}/complete${query}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/files/{fileId}/reports/{id} — отчёт по процедуре-файлу. */
export const getFileReport = async (accessToken: string, fileId: number, reportId: number): Promise<FileReport> => {
  return apiRequest(`/files/${fileId}/reports/${reportId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * Отчёты-файлы нельзя удалить (в FileReports нет DELETE): отброшенный черновик помечается полем
 * `discarded` в теле отчёта, и списки и аналитика его не показывают.
 */
export const discardFileReport = async (accessToken: string, fileId: number, reportId: number): Promise<FileReport> =>
  updateFileReport(accessToken, { fileId, reportId, patch: [{ op: 'add', path: '/discarded', value: true }] })

/**
 * Ищет незавершённый (не Sent) real-time отчёт текущего пользователя по данной процедуре —
 * используется для «продолжить» прохождение назначения с того места, где остановились.
 * Backend не хранит связь assignment↔report до завершения, поэтому эвристика:
 * последний по времени старта незавершённый отчёт, созданный этим пользователем.
 */
export const findResumableScenarioReport = async (
  accessToken: string,
  scenarioId: number,
  userId: number
): Promise<RealtimeScenarioReport | null> => {
  try {
    const list = await getScenarioReportsList(accessToken, scenarioId, { take: 100 })
    const mine = (list ?? [])
      .filter((r) => r.status !== ScenarioReportStatus.Sent && (r.creatorId === undefined || r.creatorId === userId))
      .sort((a, b) => (b.startTime ?? 0) - (a.startTime ?? 0))
    return mine[0] ?? null
  } catch {
    return null
  }
}

/**
 * Ищет последний завершённый (status=Sent) отчёт текущего пользователя по данной процедуре.
 * Используется для персистентной блокировки: если отчёт уже отправлен, процедура
 * открывается в режиме только-для-чтения при следующем посещении.
 */
export const findCompletedScenarioReport = async (
  accessToken: string,
  scenarioId: number,
  userId: number
): Promise<RealtimeScenarioReport | null> => {
  try {
    const list = await getScenarioReportsList(accessToken, scenarioId, {
      take: 50,
      statuses: [ScenarioReportStatus.Sent],
    })
    const mine = (list ?? [])
      .filter((r) => r.creatorId === undefined || r.creatorId === userId)
      .sort((a, b) => (b.endTime ?? b.startTime ?? 0) - (a.endTime ?? a.startTime ?? 0))
    return mine[0] ?? null
  } catch {
    return null
  }
}

// ============================================================
// 🪜 ШАГИ ПРОЦЕДУРЫ
// ============================================================

export interface ScenarioStepCreateRequest {
  scenarioId: number
  description?: string | null
  position?: { x: number; y: number; z: number }
  index?: number
  /** ID этапа, в который создаётся шаг (null/отсутствует — вне этапов) */
  moduleId?: number | null
  requireAnswer?: boolean
  requirePhotoReport?: boolean
}

export interface ScenarioStepUpdateRequest {
  description?: string | null
  detailedDescription?: string | null
  position?: { x: number; y: number; z: number } | null
  index?: number | null
  /** Перенос шага в другой этап */
  moduleId?: number | null
  requireAnswer?: boolean | null
  requirePhotoReport?: boolean | null
}

// ============================================================
// 📦 ЭТАПЫ ШАГОВ ПРОЦЕДУРЫ
// ============================================================

export interface ScenarioModuleCreateRequest {
  scenarioId: number
  name: string
  description?: string | null
  index?: number | null
}

export interface ScenarioModuleUpdateRequest {
  name?: string | null
  description?: string | null
  index?: number | null
}

/** POST /scenarios/modules/create */
export const createScenarioModule = async (
  accessToken: string,
  data: ScenarioModuleCreateRequest
): Promise<ScenarioModule> => {
  return apiRequest(`/scenarios/modules/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /scenarios/modules/{id}/update */
export const updateScenarioModule = async (
  accessToken: string,
  moduleId: number,
  data: ScenarioModuleUpdateRequest
): Promise<ScenarioModule> => {
  return apiRequest(`/scenarios/modules/${moduleId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** DELETE /scenarios/modules/{id} */
export const deleteScenarioModule = async (
  accessToken: string,
  moduleId: number
): Promise<void> => {
  return apiRequest(`/scenarios/modules/${moduleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/** GET /scenarios/modules/{id}/steps */
export const getScenarioModuleSteps = async (
  accessToken: string,
  moduleId: number
): Promise<ScenarioStep[]> => {
  return apiRequest(`/scenarios/modules/${moduleId}/steps`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/** POST /scenarios/modules/{id}/steps/indexes — порядок шагов внутри этапа */
export const updateModuleStepsIndexes = async (
  accessToken: string,
  moduleId: number,
  stepIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/modules/${moduleId}/steps/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(stepIds),
  })
}

/** POST /scenarios/{scenarioId}/modules/indexes — порядок этапов процедуры */
export const updateModulesIndexes = async (
  accessToken: string,
  scenarioId: number,
  moduleIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/modules/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(moduleIds),
  })
}

export interface ScenarioModuleCopyRequest {
  scenarioId: number
  name: string
  description?: string | null
  /** Шаги исходного этапа в порядке прохождения */
  stepIds: number[]
  /** Текущий порядок этапов процедуры — копия встаёт сразу за исходным */
  moduleIds: number[]
}

/**
 * Копия этапа со всеми шагами сразу за исходным. Отдельного метода в API нет:
 * создаётся этап, в него переносятся копии шагов (POST /scenarios/steps/{id}/copy).
 */
export const copyScenarioModule = async (
  accessToken: string,
  moduleId: number,
  data: ScenarioModuleCopyRequest
): Promise<ScenarioModule> => {
  const position = Math.max(0, data.moduleIds.indexOf(moduleId)) + 1
  const created = await createScenarioModule(accessToken, {
    scenarioId: data.scenarioId,
    name: data.name,
    description: data.description ?? null,
    index: position,
  })
  const copies: number[] = []
  for (const stepId of data.stepIds) {
    const copy = await copyScenarioStep(accessToken, stepId)
    await updateScenarioStep(accessToken, copy.id, { moduleId: created.id })
    copies.push(copy.id)
  }
  if (copies.length > 0) await updateModuleStepsIndexes(accessToken, created.id, copies)
  const order = data.moduleIds.filter(id => id !== created.id)
  order.splice(position, 0, created.id)
  await updateModulesIndexes(accessToken, data.scenarioId, order)
  return { ...created, index: position }
}

export const createScenarioStep = async (
  accessToken: string,
  data: ScenarioStepCreateRequest
): Promise<ScenarioStep> => {
  return apiRequest(`/scenarios/steps/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateScenarioStep = async (
  accessToken: string,
  stepId: number,
  data: ScenarioStepUpdateRequest
): Promise<ScenarioStep> => {
  return apiRequest(`/scenarios/steps/${stepId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteScenarioStep = async (
  accessToken: string,
  stepId: number
): Promise<ScenarioStep> => {
  return apiRequest(`/scenarios/steps/${stepId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const copyScenarioStep = async (
  accessToken: string,
  stepId: number
): Promise<ScenarioStep> => {
  return apiRequest(`/scenarios/steps/${stepId}/copy`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateStepsIndexes = async (
  accessToken: string,
  scenarioId: number,
  stepIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/steps/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(stepIds),
  })
}

// ============================================================
// 📋 ФОРМЫ И ВОПРОСЫ
// ============================================================

export interface FormQuestionCreateRequest {
  formId: number
  index?: number
  type: 1 | 2 | 3 | 4 | 5 | 6
  data?: string | null
  aiValidation?: boolean | null
  aiPrompt?: string | null
  caseSensitive?: boolean | null
  required?: boolean | null
  hasRightAnswer?: boolean | null
  showRightAnswer?: boolean | null
  rightAnswer?: string | null
  wrongAnswerInstruction?: string | null
  requireWrongAnswerDescription?: boolean | null
  requireWrongAnswerPhoto?: boolean | null
}

export interface FormQuestionUpdateRequest {
  index?: number | null
  type?: 1 | 2 | 3 | 4 | 5 | 6 | null
  data?: string | null
  aiValidation?: boolean | null
  aiPrompt?: string | null
  caseSensitive?: boolean | null
  required?: boolean | null
  hasRightAnswer?: boolean | null
  rightAnswer?: string | null
  showRightAnswer?: boolean | null
  wrongAnswerInstruction?: string | null
  requireWrongAnswerDescription?: boolean | null
  requireWrongAnswerPhoto?: boolean | null
  isNull?: boolean
}

export const createStepForm = async (
  accessToken: string,
  scenarioStepId: number
): Promise<ScenarioForm> => {
  return apiRequest(`/scenarios/steps/forms/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ scenarioStepId }),
  })
}

export const deleteStepForm = async (
  accessToken: string,
  formId: number
): Promise<ScenarioForm> => {
  return apiRequest(`/scenarios/steps/forms/${formId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const createFormQuestion = async (
  accessToken: string,
  data: FormQuestionCreateRequest
): Promise<FormQuestion> => {
  return apiRequest(`/scenarios/steps/forms/questions/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateFormQuestion = async (
  accessToken: string,
  questionId: number,
  data: FormQuestionUpdateRequest
): Promise<FormQuestion> => {
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteFormQuestion = async (
  accessToken: string,
  questionId: number
): Promise<FormQuestion> => {
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const copyFormQuestion = async (
  accessToken: string,
  questionId: number
): Promise<FormQuestion> => {
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}/copy`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateFormQuestionsIndexes = async (
  accessToken: string,
  formId: number,
  questionIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/steps/forms/${formId}/questions/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(questionIds),
  })
}

/**
 * Перемещает вопрос формы из одного шага в другой.
 * POST /api/v3/scenarios/steps/forms/questions/{id}/move
 */
export const moveFormQuestion = async (
  accessToken: string,
  questionId: number,
  targetStepId: number,
  index: number
): Promise<FormQuestion> => {
  const params = new URLSearchParams({
    step: String(targetStepId),
    index: String(index),
  })
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}/move?${params}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

// ============================================================
// 💬 ВАРИАНТЫ ОТВЕТА И ОБРАЗЦЫ ИЗОБРАЖЕНИЙ
// ============================================================

export interface FormQuestionAnswerCreateRequest {
  questionId: number
  index: number
  text?: string | null
  imageId?: number | null
  isRight?: boolean | null
  isValid?: boolean
}

export interface FormQuestionAnswerUpdateRequest {
  index?: number | null
  text?: string | null
  imageId?: number | null
  isRight?: boolean | null
  isNull?: boolean
}

export const createQuestionAnswer = async (
  accessToken: string,
  data: FormQuestionAnswerCreateRequest
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/scenarios/steps/forms/questions/answers/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateQuestionAnswer = async (
  accessToken: string,
  answerId: number,
  data: FormQuestionAnswerUpdateRequest
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/scenarios/steps/forms/questions/answers/${answerId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteQuestionAnswer = async (
  accessToken: string,
  answerId: number
): Promise<FormQuestionAnswer> => {
  return apiRequest(`/scenarios/steps/forms/questions/answers/${answerId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateQuestionAnswersIndexes = async (
  accessToken: string,
  questionId: number,
  answerIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}/answers/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(answerIds),
  })
}

export const createQuestionImageSample = async (
  accessToken: string,
  questionId: number,
  data: {
    index: number
    imageId: number
    isRight: boolean
  }
): Promise<FormQuestionImageSample> => {
  return apiRequest(`/scenarios/steps/forms/questions/samples/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...data, questionId }),
  })
}

export const deleteQuestionImageSample = async (
  accessToken: string,
  sampleId: number
): Promise<FormQuestionImageSample> => {
  return apiRequest(`/scenarios/steps/forms/questions/samples/${sampleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateQuestionImageSamplesIndexes = async (
  accessToken: string,
  questionId: number,
  sampleIds: number[],
  isRight: boolean
): Promise<void> => {
  const sampleType = isRight ? 'right' : 'wrong'
  return apiRequest(`/scenarios/steps/forms/questions/${questionId}/images/indexes/${sampleType}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(sampleIds),
  })
}

// ============================================================
// 📎 ВЛОЖЕНИЯ ШАГА
// ============================================================

export interface Attachment {
  id: number
  type: number
  contentId: number
  index: number
  metadata: any
  scenarioStepId: number
  objectState: number
  contentText: string | null
  subtitlesId: number | null
  content?: {
    id: number
    name: string
    type: string
    extension: string
    url: string
    staticUrl: string
    fileSize: number
    metadata: any
    s3Id: string
    shared: boolean
    groupId: number
    uploadedBy: number
    uploadedByUser: any
    createdAt: string
    updatedAt: string
    objectState: number
  }
}

export interface AttachmentCreateRequest {
  stepId: number
  type: 0 | 1 | 2 | 3
  contentId: number
  index?: number
  metadata?: string | null
  contentText?: string | null
  subtitlesId?: number | null
}

export interface AttachmentUpdateRequest {
  type?: 0 | 1 | 2 | 3 | null
  contentId?: number | null
  index?: number | null
  metadata?: string | null
  contentText?: string | null
  subtitlesId?: number | null
}

export const createStepAttachment = async (
  accessToken: string,
  data: AttachmentCreateRequest
): Promise<Attachment> => {
  return apiRequest(`/scenarios/steps/attachments/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const deleteStepAttachment = async (
  accessToken: string,
  attachmentId: number
): Promise<void> => {
  return apiRequest(`/scenarios/steps/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

export const updateStepAttachment = async (
  accessToken: string,
  attachmentId: number,
  data: AttachmentUpdateRequest
): Promise<Attachment> => {
  return apiRequest(`/scenarios/steps/attachments/${attachmentId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateAttachment = async (
  accessToken: string,
  attachmentId: number,
  data: AttachmentUpdateRequest
): Promise<Attachment> => {
  return apiRequest(`/scenarios/steps/attachments/${attachmentId}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

export const updateAttachmentsIndexes = async (
  accessToken: string,
  stepId: number,
  attachmentIds: number[]
): Promise<void> => {
  return apiRequest(`/scenarios/steps/${stepId}/attachments/indexes`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(attachmentIds),
  })
}

// ============================================================
// 📋 ВЕРСИИ И СЕССИИ РЕДАКТИРОВАНИЯ (Edit Session / VersionTree)
// ============================================================

export interface Patch {
  id: number
  /** 0=None, 1=Create, 2=Update, 3=Delete */
  type: number
  /** 0=None, 1=File, 2=Scenario, 3=ScenarioStep, 4=Attachment, 5=Form, 6=FormQuestion, 7=RightImageSample, 8=WrongImageSample, 9=Answer */
  entityType: number
  entityId: number
  /** JSON-строка с новыми значениями полей */
  data: string
  /** JSON-строка с предыдущими значениями полей (до изменения) */
  oldData?: string | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created: string
  updated: string
  objectState: number
  version: number
}

export interface EditSession {
  id: number
  patches: Patch[]
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created: string
  updated: string
  objectState: number
  description?: string | null
  version: number
}

export interface CommitSnapshot {
  id: number
  /** JSON-строка с полным состоянием процедуры на момент коммита */
  data: string
  objectState?: number
  version?: number
}

export interface Commit {
  id: number
  /** 0=None, 1=Snapshot, 2=Checkout */
  type: number
  comment?: string | null
  snapshotId?: number | null
  parentCommitId?: number | null
  snapshot?: CommitSnapshot | null
  children: Commit[]
  editSessionId?: number | null
  editSession?: EditSession | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created: string
  updated: string
  objectState: number
  version: number
}

export interface VersionTree {
  rootCommitId?: number | null
  rootCommit?: Commit | null
  activeEditSessionId?: number | null
  activeEditSession?: EditSession | null
  activeCommitId?: number | null
  commits: Commit[]
  id: number
  creatorId?: number | null
  created: string
  updated: string
  objectState: number
  version: number
}

/** POST /scenarios/{id}/versions/start — начать сессию редактирования */
export const startScenarioEditSession = async (
  accessToken: string,
  scenarioId: number
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/versions/start`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /scenarios/{id}/versions/complete — завершить сессию редактирования (создаёт новый коммит) */
export const completeScenarioEditSession = async (
  accessToken: string,
  scenarioId: number
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/versions/complete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /scenarios/{id}/versions — получить дерево версий */
export const getScenarioVersions = async (
  accessToken: string,
  scenarioId: number
): Promise<VersionTree> => {
  return apiRequest(`/scenarios/${scenarioId}/versions`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /scenarios/{id}/versions/snapshots/{snapshotId} — получить снимок версии по ID */
export const getScenarioVersionSnapshot = async (
  accessToken: string,
  scenarioId: number,
  snapshotId: number
): Promise<CommitSnapshot> => {
  return apiRequest(`/scenarios/${scenarioId}/versions/snapshots/${snapshotId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /scenarios/{id}/versions/revert?commit={commitId} — откатить процедуру к коммиту */
export const revertScenarioCommit = async (
  accessToken: string,
  scenarioId: number,
  commitId: number
): Promise<Scenario> => {
  return apiRequest(`/scenarios/${scenarioId}/versions/revert?commit=${commitId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /scenarios/{id}/versions/commits/{commitId}/session — получить сессию редактирования коммита с патчами */
export const getScenarioCommitSession = async (
  accessToken: string,
  scenarioId: number,
  commitId: number
): Promise<EditSession> => {
  return apiRequest(`/scenarios/${scenarioId}/versions/commits/${commitId}/session`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

export interface ScenarioAtTimeResult {
  scenario: Scenario
  /** Номер версии коммита (для отображения в UI, например «v17»). null если версионирование не применялось. */
  commitVersion: number | null
}

/**
 * Загружает снапшот процедуры, актуальный на момент отправки отчёта.
 * Находит последний коммит со снапшотом, созданный не позже времени отчёта.
 * Если подходящего снапшота нет — возвращает текущую версию процедуры.
 */
export const getScenarioAtTime = async (
  accessToken: string,
  scenarioId: number,
  reportTimestampSec: number
): Promise<ScenarioAtTimeResult> => {
  try {
    const versionTree = await getScenarioVersions(accessToken, scenarioId)
    const allCommits = versionTree.commits ?? []
    const snapshotCommits = allCommits
      .filter(c => c.snapshotId != null && c.created)
      .sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())

    const reportTimeMs = reportTimestampSec * 1000
    const matchingCommit = snapshotCommits.find(
      c => new Date(c.created).getTime() <= reportTimeMs
    )

    if (matchingCommit?.snapshotId != null) {
      const snapshot = await getScenarioVersionSnapshot(accessToken, scenarioId, matchingCommit.snapshotId)
      if (snapshot?.data) {
        const parsed = JSON.parse(snapshot.data) as Scenario
        // Снапшот хранит шаги в порядке создания, а не по index — сортируем
        // (с учётом этапов: порядок этапов, затем index внутри этапа)
        if (Array.isArray(parsed?.steps)) {
          parsed.steps = withOrderedSteps(parsed, { includeDeleted: true }).steps
        }
        if (parsed) {
          // Версия = порядковый номер коммита по дате (от 1)
          const sortedByDate = [...allCommits].sort(
            (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()
          )
          const commitVersion = sortedByDate.findIndex(c => c.id === matchingCommit.id) + 1
          return { scenario: parsed, commitVersion: commitVersion > 0 ? commitVersion : null }
        }
      }
    }
  } catch {
    // Нет снапшота или ошибка — возвращаем актуальную процедуру
  }
  const scenario = await getScenario(accessToken, scenarioId)
  return { scenario, commitVersion: null }
}

// ============================================================
// 📁 FILE VERSIONING — версионирование файлов
// ============================================================

/** POST /files/{fileId}/update — обновить содержимое файла (загрузить новую версию контента) */
export const updateAiFileContent = async (
  accessToken: string,
  fileId: number,
  file: File
): Promise<void> => {
  const url = `${API_BASE_URL}/files/${fileId}/update`
  const formData = new FormData()
  formData.append('File', file)

  // Не задаём Content-Type — браузер сам выставит multipart/form-data с boundary
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    let message = errorText?.trim() || `HTTP ${response.status}`
    try {
      const parsed = JSON.parse(errorText)
      if (parsed?.message) message = String(parsed.message)
    } catch { /* не JSON */ }
    throw new ApiError(response.status, message, { raw: errorText })
  }
}

/** GET /files/{fileId}/versions — получить дерево версий файла */
export const getFileVersions = async (
  accessToken: string,
  fileId: number
): Promise<VersionTree> => {
  return apiRequest(`/files/${fileId}/versions`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /files/{fileId}/versions/start — начать сессию редактирования файла */
export const startFileEditSession = async (
  accessToken: string,
  fileId: number
): Promise<void> => {
  return apiRequest(`/files/${fileId}/versions/start`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /files/{fileId}/versions/complete — завершить сессию редактирования (создаёт коммит) */
export const completeFileEditSession = async (
  accessToken: string,
  fileId: number
): Promise<void> => {
  return apiRequest(`/files/${fileId}/versions/complete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /files/{fileId}/versions/commits/{commitId}/session — получить сессию редактирования коммита с патчами */
export const getFileCommitSession = async (
  accessToken: string,
  fileId: number,
  commitId: number
): Promise<EditSession> => {
  return apiRequest(`/files/${fileId}/versions/commits/${commitId}/session`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /files/{fileId}/versions/revert?commit={commitId} — откатить файл к коммиту */
export const revertFileCommit = async (
  accessToken: string,
  fileId: number,
  commitId: number
): Promise<void> => {
  return apiRequest(`/files/${fileId}/versions/revert?commit=${commitId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ============================================================
// 🤖 AI ASK — простой запрос к AI (без стриминга)
// ============================================================

export interface AskRequest {
  /** Вопрос или запрос к AI */
  question: string
  /** Игнорировать процедуры при поиске */
  ignoreScenarios?: boolean
  /** Игнорировать инциденты при поиске */
  ignoreIncidents?: boolean
  /** Игнорировать документы при поиске */
  ignoreDocuments?: boolean
}

/**
 * POST /ai/ask — отправить вопрос AI и получить ответ.
 * Документация объявляет ответ как string, но сервер фактически возвращает
 * массив объектов-чанков. Используем unknown и разбираем на стороне вызывающего.
 * Параметр workspace передаётся как query-параметр.
 */
export const askAi = async (
  accessToken: string,
  request: AskRequest,
  workspaceId?: number
): Promise<unknown> => {
  const params = new URLSearchParams()
  if (workspaceId !== undefined) params.set('workspace', String(workspaceId))
  if (request.ignoreScenarios !== undefined) params.set('ignoreScenarios', String(request.ignoreScenarios))
  if (request.ignoreIncidents !== undefined) params.set('ignoreIncidents', String(request.ignoreIncidents))
  if (request.ignoreDocuments !== undefined) params.set('ignoreDocuments', String(request.ignoreDocuments))
  const qs = params.toString() ? `?${params.toString()}` : ''
  debugLog('api', `🤖 askAi → POST /ai/ask${qs}`)
  const { ignoreScenarios: _s, ignoreIncidents: _i, ignoreDocuments: _d, ...body } = request
  return apiRequest(`/ai/ask${qs}`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  })
}

// ============================================================
// 🤖 AI CHAT — AiChat (стриминг)
// ============================================================

export interface ChatMessage {
  role: string
  content: unknown
}

export interface ChatRequest {
  model?: string | null
  messages: ChatMessage[]
  useWebSearch?: boolean | null
  useCodeInterpreter?: boolean | null
  reasoningEffort?: string | null
  reasoningSummary?: string | null
  verbosity?: string | null
  useRag?: boolean | null
  workspaceId?: number | null
  groupId?: number | null
}

/** POST /ai/chat/stream — стриминговый ответ AI (SSE). Возвращает raw Response. */
export const streamAiChat = async (
  accessToken: string,
  request: ChatRequest
): Promise<Response> => {
  const url = `${API_BASE_URL}/ai/chat/stream`
  debugLog('api', '🤖 streamAiChat →', url)
  return fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
}

/** GET /ai/chat/local/session — получить токен локальной сессии */
export const getAiLocalSession = async (
  accessToken: string
): Promise<string> => {
  return apiRequest('/ai/chat/local/session', {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })
}

/** POST /ai/chat/local/stream — стриминг через локальную модель (SSE). Возвращает raw Response. */
export const streamAiLocalChat = async (
  accessToken: string,
  session: string,
  request: ChatRequest
): Promise<Response> => {
  const url = `${API_BASE_URL}/ai/chat/local/stream?session=${encodeURIComponent(session)}`
  debugLog('api', '🤖 streamAiLocalChat →', url)
  return fetch(url, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  })
}

// ============================================================
// 💬 AI DIALOGS — типы
// ============================================================

export interface DialogParams {
  model?: string | null
  forceUseKnowledgeBase?: boolean | null
}

export interface Dialog {
  id: number
  name: string
  state: number
  workspaceId: number
  rootMessageId?: number | null
  rootMessage?: AiMessage | null
  activeMessageId: number
  tools?: AiTool[] | null
  params?: DialogParams | null
  creatorId?: number | null
  created: string
  updated: string
  objectState: number
  version: number
}

export interface AiMessage {
  id: number
  role: number
  dialogId: number
  parentMessageId?: number | null
  items: AiMessageItem[]
  children: AiMessage[]
  creatorId?: number | null
  created: string
  updated: string
  objectState: number
  version: number
}

export interface AiMessageItem {
  type: number
  text?: string | null
  url?: string | null
  assetId?: number | null
  fileId?: number | null
}

export interface AiTool {
  id: number
  name: string
  description?: string | null
  key: string
  dialogId: number
  json: string
  creatorId?: number | null
  created: string
  updated: string
  objectState: number
  version: number
}

export interface PaginatorResultOfDialog {
  items: Dialog[]
  total: number
  skip: number
  take: number
}

export interface CreateDialogRequest {
  workspaceId: number
  name?: string | null
  instruction?: string | null
  params?: DialogParams | null
}

export interface UpdateDialogRequest {
  name?: string | null
  params?: DialogParams | null
}

export interface AiMessageItemRequest {
  text?: string | null
  assetId?: number | null
  fileId?: number | null
  url?: string | null
}

export interface CreateMessageRequest {
  dialogId: number
  parentMessageId?: number | null
  items: AiMessageItemRequest[]
}

export interface ToolCallResultRequest {
  id: string
  result: string
}

export interface CreateToolRequest {
  name: string
  description?: string | null
  key: string
  json: string
}

export interface UpdateToolRequest {
  name?: string | null
  description?: string | null
  key?: string | null
  json?: string | null
}

// ============================================================
// 💬 AI DIALOGS — CRUD
// ============================================================

export interface GetDialogsParams {
  workspace?: number
  sort?: number
  skip?: number
  take?: number
}

/** GET /ai/dialogs — диалоги текущего пользователя */
export const getDialogs = async (
  accessToken: string,
  params: GetDialogsParams = {}
): Promise<PaginatorResultOfDialog> => {
  const query = new URLSearchParams()
  if (params.workspace != null) query.set('workspace', String(params.workspace))
  if (params.sort != null) query.set('sort', String(params.sort))
  if (params.skip != null) query.set('skip', String(params.skip))
  if (params.take != null) query.set('take', String(params.take))
  const qs = query.toString()
  return apiRequest(`/ai/dialogs${qs ? `?${qs}` : ''}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

export interface GetAllDialogsParams extends GetDialogsParams {
  user?: number
}

/** GET /ai/dialogs/all — все диалоги (admin) */
export const getAllDialogs = async (
  accessToken: string,
  params: GetAllDialogsParams = {}
): Promise<PaginatorResultOfDialog> => {
  const query = new URLSearchParams()
  if (params.user != null) query.set('user', String(params.user))
  if (params.workspace != null) query.set('workspace', String(params.workspace))
  if (params.sort != null) query.set('sort', String(params.sort))
  if (params.skip != null) query.set('skip', String(params.skip))
  if (params.take != null) query.set('take', String(params.take))
  const qs = query.toString()
  return apiRequest(`/ai/dialogs/all${qs ? `?${qs}` : ''}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** GET /ai/dialogs/{id} — получить диалог */
export const getDialog = async (
  accessToken: string,
  id: number
): Promise<Dialog> => {
  return apiRequest(`/ai/dialogs/${id}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** DELETE /ai/dialogs/{id} — удалить диалог */
export const deleteDialog = async (
  accessToken: string,
  id: number
): Promise<void> => {
  return apiRequest(`/ai/dialogs/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** POST /ai/dialogs/create — создать диалог */
export const createDialog = async (
  accessToken: string,
  data: CreateDialogRequest
): Promise<Dialog> => {
  return apiRequest('/ai/dialogs/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /ai/dialogs/{id}/update — обновить диалог */
export const updateDialog = async (
  accessToken: string,
  id: number,
  data: UpdateDialogRequest
): Promise<Dialog> => {
  return apiRequest(`/ai/dialogs/${id}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ============================================================
// 💬 AI DIALOGS — сообщения и стриминг
// ============================================================

/** POST /ai/dialogs/messages/create — создать сообщение и запустить ответ AI */
export const createAiMessage = async (
  accessToken: string,
  data: CreateMessageRequest
): Promise<AiMessage> => {
  return apiRequest('/ai/dialogs/messages/create', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** GET /ai/dialogs/{id}/stream — SSE стрим активного диалога. Возвращает raw Response. */
export const getDialogStream = async (
  accessToken: string,
  id: number
): Promise<Response> => {
  const url = `${API_BASE_URL}/ai/dialogs/${id}/stream`
  debugLog('api', `💬 getDialogStream → ${url}`)
  return fetch(url, {
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'text/event-stream',
    },
  })
}

/** POST /ai/dialogs/tools/result — отправить результат вызова инструмента */
export const sendToolCallResult = async (
  accessToken: string,
  data: ToolCallResultRequest
): Promise<void> => {
  return apiRequest('/ai/dialogs/tools/result', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ============================================================
// 🔧 AI DIALOGS — инструменты (Tools)
// ============================================================

/** GET /ai/dialogs/{dialogId}/tools/{id} — получить инструмент */
export const getDialogTool = async (
  accessToken: string,
  dialogId: number,
  id: number
): Promise<AiTool> => {
  return apiRequest(`/ai/dialogs/${dialogId}/tools/${id}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** DELETE /ai/dialogs/{dialogId}/tools/{id} — удалить инструмент */
export const deleteDialogTool = async (
  accessToken: string,
  dialogId: number,
  id: number
): Promise<void> => {
  return apiRequest(`/ai/dialogs/${dialogId}/tools/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** POST /ai/dialogs/{dialogId}/tools/create — создать инструмент */
export const createDialogTool = async (
  accessToken: string,
  dialogId: number,
  data: CreateToolRequest
): Promise<AiTool> => {
  return apiRequest(`/ai/dialogs/${dialogId}/tools/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /ai/dialogs/{dialogId}/tools/{id}/update — обновить инструмент */
export const updateDialogTool = async (
  accessToken: string,
  dialogId: number,
  id: number,
  data: UpdateToolRequest
): Promise<AiTool> => {
  return apiRequest(`/ai/dialogs/${dialogId}/tools/${id}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ============================================================
// 🔒 PERMISSIONS — управление правами доступа к ресурсам
// ============================================================

export interface ResourcePermission {
  id: number
  owner: number        // 0=None 1=User 2=Group 3=Workspace 4=Role
  target: number       // 0=None 1=Folder 2=Scenario 3=Note 4=Area 5=File 6=List
  value: number        // flags: 1=Read 2=Comment 4=Edit 8=Delete 16=Execute
  parentWorkspaceId?: number | null
  folderId?: number | null
  scenarioId?: number | null
  noteId?: number | null
  areaId?: number | null
  fileId?: number | null
  listId?: number | null
  userId?: number | null
  groupId?: number | null
  workspaceId?: number | null
  roleId?: number | null
  creatorId?: number | null
  creator?: { id?: number; name?: string; login?: string } | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface ResourcePermissionCreateRequest {
  owner: number
  ownerId: number
  target: number
  targetId: number
  value: number
  workspaceId: number
}

export interface ResourcePermissionUpdateRequest {
  value: number
}

/** POST /permissions/create */
export const createResourcePermission = async (
  accessToken: string,
  data: ResourcePermissionCreateRequest
): Promise<ResourcePermission> => {
  return apiRequest(`/permissions/create`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** GET /permissions/{id} */
export const getResourcePermission = async (
  accessToken: string,
  id: number
): Promise<ResourcePermission> => {
  return apiRequest(`/permissions/${id}`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** POST /permissions/{id}/update */
export const updateResourcePermission = async (
  accessToken: string,
  id: number,
  data: ResourcePermissionUpdateRequest
): Promise<ResourcePermission> => {
  return apiRequest(`/permissions/${id}/update`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** DELETE /permissions/{id} */
export const deleteResourcePermission = async (
  accessToken: string,
  id: number
): Promise<void> => {
  await apiRequest(`/permissions/${id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** GET /areas/{id}/permissions */
export const getAreaPermissions = async (
  accessToken: string,
  areaId: number
): Promise<ResourcePermission[]> => {
  return apiRequest(`/areas/${areaId}/permissions`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** GET /files/{fileId}/permissions */
export const getFilePermissions = async (
  accessToken: string,
  fileId: number
): Promise<ResourcePermission[]> => {
  return apiRequest(`/files/${fileId}/permissions`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** GET /folders/{folderId}/permissions */
export const getFolderPermissions = async (
  accessToken: string,
  folderId: number
): Promise<ResourcePermission[]> => {
  return apiRequest(`/folders/${folderId}/permissions`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}

/** GET /scenarios/{id}/permissions */
export const getScenarioPermissions = async (
  accessToken: string,
  scenarioId: number
): Promise<ResourcePermission[]> => {
  return apiRequest(`/scenarios/${scenarioId}/permissions`, {
    method: 'GET',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  })
}


// ============================================================
// 📦 MOVE — перемещение файлов и папок в другую папку
// ============================================================

/** POST /folders/{folderId}/update — переместить папку (сменить parentId) */
export const updateFolderParent = async (
  accessToken: string,
  folderId: number,
  parentId: number | null
): Promise<void> => {
  const url = `${API_BASE_URL}/folders/${folderId}/update`
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ parentId }),
  })
  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw new Error(`Ошибка перемещения папки: ${response.status} ${errorText}`)
  }
}

/** POST /files/{fileId}/update — переместить файл в другую папку (только FolderId) */
export const moveAiFileToFolder = async (
  accessToken: string,
  fileId: number,
  targetFolderId: number
): Promise<void> => {
  const url = `${API_BASE_URL}/files/${fileId}/update`
  const formData = new FormData()
  formData.append('FolderId', targetFolderId.toString())
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  })
  if (!response.ok) {
    const errorText = await response.text().catch(() => '')
    throw new Error(`Ошибка перемещения файла: ${response.status} ${errorText}`)
  }
}

// ============================================================
// 🚨 ИНЦИДЕНТЫ (Incidents API v3)
// ============================================================

export interface AssetV3 {
  id: number
  name: string
  type: string
  extension: string
  metadata?: string | null
  isPublic?: boolean
  s3Id?: string
  workspaceId?: number | null
  fileSize?: number
  url?: string | null
  staticUrl?: string | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface IncidentTypeFieldV3 {
  id: number
  incidentTypeId: number
  name: string
  /** 1=Text, 2=Number, 3=SingleSelect, 4=MultiSelect, 5=Attachment, 6=User */
  fieldType: number
  required: boolean
  index: number
  /** JSON options for SingleSelect / MultiSelect fields */
  options?: string | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created?: string
  updated?: string
  objectState?: number
  version?: number
}

export interface IncidentTypeV3 {
  id: number
  name: string
  workspaceId?: number | null
  fields: IncidentTypeFieldV3[]
  activityCount?: number
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created?: string
  updated?: string
  objectState?: number
  objectStatusId?: number | null
  description?: string | null
  version?: number
}

export interface IncidentAttachmentV3 {
  id: number
  incidentId: number
  index: number
  /** 0=None, 1=Photo, 2=Video, 3=File */
  type: number
  contentId?: number | null
  content?: AssetV3 | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created?: string
  updated?: string
  objectState?: number
  description?: string | null
  version?: number
}

export interface IncidentDecisionAttachment {
  id: number
  incidentId: number
  index: number
  /** 0=None, 1=Photo, 2=Video, 3=File */
  type: number
  contentId?: number | null
  content?: AssetV3 | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created?: string
  updated?: string
  objectState?: number
  description?: string | null
  version?: number
}

export interface IncidentFieldValueV3 {
  incidentTypeFieldId: number
  value: string | null
}

export interface IncidentV3 {
  id: number
  areaId: number
  name: string
  /** priority integer: 1=Low, 2=Medium, 3=High, 4=Critical */
  priority: number
  incidentTypeId?: number | null
  incidentType?: IncidentTypeV3 | null
  /** Final decision / resolution text */
  decision?: string | null
  responsibleUserId?: number | null
  responsibleUser?: { id: number; name: string; login: string } | null
  approverUserId?: number | null
  approverUser?: { id: number; name: string; login: string } | null
  /** Ответственная группа */
  responsibleGroupId?: number | null
  responsibleGroup?: GroupV3 | null
  /** Утверждающая группа */
  approverGroupId?: number | null
  approverGroup?: GroupV3 | null
  /** Наблюдатели инцидента */
  observers?: { id: number; name: string; login: string }[] | null
  /** Наблюдающие группы инцидента */
  observerGroups?: GroupV3[] | null
  /** 0=None, 1=Pending, 2=InProgress, 3=AwaitApproval, 4=Resolved */
  status: number
  metadata?: string | null
  fieldValues?: IncidentFieldValueV3[] | null
  attachments: IncidentAttachmentV3[]
  decisionAttachments?: IncidentDecisionAttachment[] | null
  creatorId?: number | null
  creator?: { id: number; name: string; login: string } | null
  created: string
  updated: string
  objectState: number
  objectStatusId?: number | null
  objectStatus?: ObjectStatusDto | null
  description?: string | null
  version: number
  /** Привязанная процедура (если инцидент создан из прохождения процедуры) */
  scenarioId?: number | null
  scenario?: { id: number; name: string } | null
  /** Привязанный шаг процедуры */
  scenarioStepId?: number | null
  /** name у шага обычно пустой — реальное отображаемое название лежит в description */
  scenarioStep?: { id: number; name?: string | null; description?: string | null; index?: number } | null
  /** Привязанный вопрос формы шага */
  formQuestionId?: number | null
  formQuestion?: { id: number; data?: string | null } | null
  /** Накопленное суммарное время в статусе InProgress (мс) */
  inProgressDurationMs?: number | null
  /** Время закрытия инцидента (UTC) */
  closedAt?: string | null
  /** Момент последнего перехода в InProgress */
  inProgressStartedAt?: string | null
  /** Плановая дата возобновления работ (UTC) */
  plannedResumeAt?: string | null
}

export interface IncidentCreateRequest {
  /** Участок; без него инцидент создаётся в воркспейсе `workspaceId` */
  areaId?: number | null
  name: string
  priority?: number | null
  incidentTypeId?: number | null
  decision?: string | null
  responsibleUserId?: number | null
  approverUserId?: number | null
  /** Ответственная группа (allowBeIncidentsResolver=true) */
  responsibleGroupId?: number | null
  /** Утверждающая группа (allowBeIncidentsApprover=true) */
  approverGroupId?: number | null
  /** ID наблюдателей */
  observerUserIds?: number[] | null
  /** ID групп-наблюдателей */
  observerGroupIds?: number[] | null
  status?: number | null
  metadata?: string | null
  description?: string | null
  fieldValues?: IncidentFieldValueV3[] | null
  /** Привязка к процедуре/шагу/вопросу (значения <= 0 сбрасывают привязку) */
  scenarioId?: number | null
  scenarioStepId?: number | null
  formQuestionId?: number | null
  /** Воркспейс; если не задан — определяется по areaId */
  workspaceId?: number | null
}

export interface IncidentUpdateRequest {
  name?: string | null
  priority?: number | null
  incidentTypeId?: number | null
  decision?: string | null
  responsibleUserId?: number | null
  approverUserId?: number | null
  /** Ответственная группа (allowBeIncidentsResolver=true). null или <= 0 сбрасывают группу */
  responsibleGroupId?: number | null
  /** Утверждающая группа (allowBeIncidentsApprover=true). null или <= 0 сбрасывают группу */
  approverGroupId?: number | null
  /** ID наблюдателей. Пустой массив удаляет всех наблюдателей */
  observerUserIds?: number[] | null
  /** ID групп-наблюдателей. Пустой массив удаляет все группы-наблюдатели */
  observerGroupIds?: number[] | null
  status?: number | null
  metadata?: string | null
  description?: string | null
  areaId?: number | null
  fieldValues?: IncidentFieldValueV3[] | null
  /** Привязка к процедуре/шагу/вопросу (значения <= 0 сбрасывают привязку) */
  scenarioId?: number | null
  scenarioStepId?: number | null
  formQuestionId?: number | null
  /** Плановая дата возобновления работ (UTC). Explicit null clears the value */
  plannedResumeAt?: string | null
}

export interface IncidentAttachmentUploadParams {
  file: File
  incidentId: number
  index: number
  onProgress?: (progress: number) => void
}

export type IncidentDecisionAttachmentUploadParams = IncidentAttachmentUploadParams

export interface PaginatorResultV3<T> {
  totalCount: number
  result: T[] | null
}

/** GET /api/v3/incidents — список инцидентов (пагинированный ответ) */
export const getIncidents = async (
  accessToken: string,
  params: {
    workspace?: number
    edit?: boolean
    skip?: number
    take?: number
    /** Sort by creation time: >= 0 ascending, < 0 descending */
    sort?: number
    /** Begin creation time (Unix UTC ms) */
    begin?: number
    /** End creation time (Unix UTC ms) */
    end?: number
    /** Filter by creator user IDs */
    creator?: number[]
    /** Filter by responsible user IDs */
    responsible?: number[]
    /** Filter by approver user IDs */
    approver?: number[]
    /** Filter by responsible group IDs */
    responsibleGroup?: number[]
    /** Filter by approver group IDs */
    approverGroup?: number[]
    /** Filter by observer user IDs (наблюдатели) */
    observer?: number[]
    /** Only incidents assigned to current user (mine=true) */
    mine?: boolean
    /** Only incidents assigned to current user's group */
    myGroup?: boolean
    /** Filter by incident type IDs */
    type?: number[]
    /** Filter by area IDs */
    area?: number[]
    /** Filter by incident statuses (0=None, 1=Pending, 2=InProgress, 3=AwaitApproval, 4=Resolved, 5=Deferred, 6=AwaitingParts) */
    status?: number[]
    /** Filter by incident priorities (1=Low, 2=Medium, 3=High, 4=Critical) */
    priority?: number[]
    /** Full-text search query */
    query?: string
  } = {}
): Promise<PaginatorResultV3<IncidentV3>> => {
  const qs = new URLSearchParams()
  if (params.workspace  !== undefined) qs.set('workspace', String(params.workspace))
  if (params.edit       !== undefined) qs.set('edit', String(params.edit))
  if (params.skip       !== undefined) qs.set('skip', String(params.skip))
  if (params.take       !== undefined) qs.set('take', String(params.take))
  if (params.sort       !== undefined) qs.set('sort', String(params.sort))
  if (params.begin      !== undefined) qs.set('begin', String(params.begin))
  if (params.end        !== undefined) qs.set('end', String(params.end))
  if (params.mine       !== undefined) qs.set('mine', String(params.mine))
  if (params.myGroup    !== undefined) qs.set('myGroup', String(params.myGroup))
  if (params.query)                    qs.set('query', params.query)
  params.creator?.forEach(id              => qs.append('creator', String(id)))
  params.responsible?.forEach(id         => qs.append('responsible', String(id)))
  params.approver?.forEach(id            => qs.append('approver', String(id)))
  params.responsibleGroup?.forEach(id    => qs.append('responsibleGroup', String(id)))
  params.approverGroup?.forEach(id       => qs.append('approverGroup', String(id)))
  params.observer?.forEach(id            => qs.append('observer', String(id)))
  params.type?.forEach(id                => qs.append('type', String(id)))
  params.area?.forEach(id                => qs.append('area', String(id)))
  params.status?.forEach(id              => qs.append('status', String(id)))
  params.priority?.forEach(id            => qs.append('priority', String(id)))
  const queryStr = qs.toString() ? `?${qs.toString()}` : ''
  return apiRequest(`/incidents${queryStr}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/{id} — один инцидент */
export const getIncident = async (
  accessToken: string,
  id: number
): Promise<IncidentV3> => {
  return apiRequest(`/incidents/${id}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** DELETE /api/v3/incidents/{id} — удалить инцидент */
export const deleteIncident = async (
  accessToken: string,
  id: number
): Promise<void> => {
  return apiRequest(`/incidents/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ─── Incident Analytics ──────────────────────────────────────────────────────

/** Статус инцидента: 0=None, 1=Pending, 2=InProgress, 3=AwaitApproval, 4=Resolved, 5=Deferred, 6=AwaitingParts */
export type IncidentStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6

export interface IncidentAnalyticsStatusCount {
  /** 0=None, 1=Pending, 2=InProgress, 3=AwaitApproval, 4=Resolved, 5=Deferred, 6=AwaitingParts */
  status: IncidentStatus
  /** Количество инцидентов с данным статусом */
  count: number
}

export interface IncidentAnalyticsDailyCount {
  /** UTC-дата (calendar day) */
  date: string
  /** Количество созданных инцидентов за день */
  count: number
}

export interface IncidentAnalyticsNamedCount {
  /** ID сущности (типа инцидента) */
  id: number | null
  /** Название сущности */
  name: string
  /** Количество инцидентов */
  count: number
}

export interface IncidentAnalyticsAreaMetrics {
  /** ID участка */
  id: number | null
  /** Название участка */
  name: string
  /** Общее количество созданных инцидентов */
  total: number
  /** Количество закрытых (Resolved) инцидентов */
  completed: number
  /** Процент закрытых инцидентов */
  completedPercent: number
  /** Среднее время нахождения в статусе InProgress (мс) */
  averageResolutionDurationMs: number
  /** Среднее время жизни закрытых инцидентов (мс) */
  averageLifetimeDurationMs: number
}

export interface IncidentAnalyticsResponse {
  /** Начало периода (UTC) */
  begin?: string | null
  /** Конец периода (UTC) */
  end?: string | null
  /** Всего создано инцидентов за период */
  totalCreated?: number | null
  /** Распределение по текущим статусам */
  statusCounts?: IncidentAnalyticsStatusCount[] | null
  /** Среднее время InProgress (мс) */
  averageResolutionDurationMs?: number | null
  /** Среднее время нахождения в статусе Deferred (мс) */
  averageDeferredDurationMs?: number | null
  /** Среднее время нахождения в статусе AwaitingParts (мс) */
  averageAwaitingPartsDurationMs?: number | null
  /** Среднее время жизни закрытых инцидентов (мс) */
  averageLifetimeDurationMs?: number | null
  /** Процент закрытых инцидентов */
  completedPercent?: number | null
  /** Количество созданных инцидентов по UTC-дням */
  dailyFrequency?: IncidentAnalyticsDailyCount[] | null
  /** Распределение по типам инцидентов */
  typeDistribution?: IncidentAnalyticsNamedCount[] | null
  /** Метрики по участкам */
  areas?: IncidentAnalyticsAreaMetrics[] | null
}

/** GET /api/v3/analytics/incidents — аналитика инцидентов за период создания */
export const getIncidentAnalytics = async (
  accessToken: string,
  params: {
    /** ID рабочего пространства */
    workspace?: number
    /** Начало периода в Unix UTC миллисекундах */
    begin?: number
    /** Конец периода в Unix UTC миллисекундах */
    end?: number
    /** Фильтр по текущим статусам инцидентов */
    status?: IncidentStatus[]
    /** Фильтр по ID участков */
    area?: number[]
    /** Фильтр по ID типов инцидентов */
    type?: number[]
  } = {}
): Promise<IncidentAnalyticsResponse> => {
  const qs = new URLSearchParams()
  if (params.workspace !== undefined) qs.set('workspace', String(params.workspace))
  if (params.begin     !== undefined) qs.set('begin', String(params.begin))
  if (params.end       !== undefined) qs.set('end', String(params.end))
  params.status?.forEach(s  => qs.append('status', String(s)))
  params.area?.forEach(id   => qs.append('area', String(id)))
  params.type?.forEach(id   => qs.append('type', String(id)))
  const queryStr = qs.toString() ? `?${qs.toString()}` : ''
  return apiRequest(`/analytics/incidents${queryStr}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ─── Activity ────────────────────────────────────────────────────────────────

export interface ActivityChange {
  field: string
  oldValue: string
  newValue: string
}

export interface ActivityAttachment {
  id: number
  activityId: number
  index: number
  type: number  // 0=None, 1=Photo, 2=Video, 3=File
  contentId?: number | null
  content?: { url?: string | null; staticUrl?: string | null; name?: string | null } | null
  creatorId?: number | null
  creator?: { id?: number; name?: string } | null
  created: string
  updated: string
  objectState?: number
}

/** Activity type: 1=Created, 2=Updated, 3=Comment */
export interface IncidentActivity {
  id: number
  parentActivityFeedId: number
  /** 1=Created, 2=Updated, 3=Comment, 4=ApprovalFromScenarioReport */
  type: 1 | 2 | 3 | 4
  comment?: string | null
  attachments?: ActivityAttachment[]
  changes?: ActivityChange[] | null
  /** Присутствует только для type=4 */
  customEventData?: {
    value: boolean
    reportId: number
    scenarioId: number
  } | null
  creatorId?: number | null
  creator?: { id?: number; name?: string } | null
  created: string
  updated: string
  objectState?: number
}

/** GET /api/v3/incidents/{incidentId}/activity */
export const getIncidentActivity = async (
  accessToken: string,
  incidentId: number,
  params: { take?: number; skip?: number } = {}
): Promise<{ total: number; items: IncidentActivity[] }> => {
  const qs = new URLSearchParams()
  if (params.take !== undefined) qs.set('take', String(params.take))
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  const q = qs.toString() ? `?${qs.toString()}` : ''
  return apiRequest(`/incidents/${incidentId}/activity${q}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/activity/comments */
export const createIncidentComment = async (
  accessToken: string,
  incidentId: number,
  comment: string,
  files?: File[]
): Promise<IncidentActivity> => {
  const form = new FormData()
  form.append('Comment', comment)
  files?.forEach(file => form.append('attachment', file))
  return apiRequest(`/incidents/${incidentId}/activity/comments`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: form,
  })
}

/** POST /api/v3/incidents/{incidentId}/activity/approval-from-scenario-report */
export const addApprovalFromScenarioReportActivity = async (
  accessToken: string,
  incidentId: number,
  data: {
    /** true — подтверждён, false — отклонён */
    value: boolean
    scenarioId: number
    reportId: number
  }
): Promise<IncidentActivity> => {
  return apiRequest(`/incidents/${incidentId}/activity/approval-from-scenario-report`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** DELETE /api/v3/incidents/{incidentId}/activity/comments/{activityId} */
export const deleteIncidentComment = async (
  accessToken: string,
  incidentId: number,
  activityId: number
): Promise<void> => {
  return apiRequest(`/incidents/${incidentId}/activity/comments/${activityId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/{id}/permissions — права на инцидент */
export const getIncidentPermissions = async (
  accessToken: string,
  id: number
): Promise<ResourcePermission[]> => {
  return apiRequest(`/incidents/${id}/permissions`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/create — создать инцидент */
export const createIncident = async (
  accessToken: string,
  data: IncidentCreateRequest
): Promise<IncidentV3> => {
  return apiRequest('/incidents/create', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /api/v3/incidents/{id}/update — обновить инцидент */
export const updateIncident = async (
  accessToken: string,
  id: number,
  data: IncidentUpdateRequest
): Promise<IncidentV3> => {
  return apiRequest(`/incidents/${id}/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /api/v3/incidents/attachments/create — загрузить вложение инцидента (multipart/form-data).
 *  Прямой XHR на API, аналогично uploadAsset в процедурах. Поддерживает progress-callback. */
export const createIncidentAttachment = (
  accessToken: string,
  params: IncidentAttachmentUploadParams
): Promise<IncidentAttachmentV3> => {
  const url = `${API_BASE_URL}/incidents/attachments/create`

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && params.onProgress) {
        params.onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: POST /incidents/attachments/create → ${xhr.status}`)
          debugLog('api', '📊 Status:', xhr.status)
          debugLog('api', '✅ OK:', true)
          debugGroupEnd('api')
        }
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Ошибка парсинга ответа сервера'))
        }
      } else {
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: POST /incidents/attachments/create → ${xhr.status}`)
          debugLog('api', '📊 Status:', xhr.status)
          debugLog('api', '✅ OK:', false)
          debugLog('api', '❌ Error Body:', xhr.responseText)
          debugGroupEnd('api')
        }
        reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText || xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Ошибка сетевого соединения')))
    xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')))

    const formData = new FormData()
    formData.append('File', params.file)
    formData.append('Index', String(params.index))
    formData.append('IncidentId', String(params.incidentId))

    if (DEBUG.shouldLog('api')) {
      debugGroup('api', `🚀 API v3 REQUEST: POST /incidents/attachments/create`)
      debugLog('api', '📍 URL:', url)
      debugLog('api', '📦 File:', params.file.name, 'size:', params.file.size)
      debugLog('api', '📄 IncidentId:', params.incidentId, 'Index:', params.index)
      debugGroupEnd('api')
    }
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.send(formData)
  })
}

/** GET /api/v3/incidents/attachments/{attachmentId} — получить вложение по ID (включая удалённые) */
export const getIncidentAttachment = async (
  accessToken: string,
  attachmentId: number
): Promise<IncidentAttachmentV3> => {
  return apiRequest(`/incidents/attachments/${attachmentId}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** DELETE /api/v3/incidents/attachments/{attachmentId} — удалить вложение */
export const deleteIncidentAttachment = async (
  accessToken: string,
  attachmentId: number
): Promise<void> => {
  return apiRequest(`/incidents/attachments/${attachmentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/attachments/indexes — обновить порядок вложений */
export const updateIncidentAttachmentIndexes = async (
  accessToken: string,
  incidentId: number,
  ids: number[]
): Promise<void> => {
  return apiRequest(`/incidents/${incidentId}/attachments/indexes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ids),
  })
}

/** POST /api/v3/incidents/{incidentId}/attachments/decision/indexes — обновить порядок вложений решения */
export const updateDecisionAttachmentIndexes = async (
  accessToken: string,
  incidentId: number,
  ids: number[]
): Promise<void> => {
  return apiRequest(`/incidents/${incidentId}/decision/attachments/indexes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(ids),
  })
}

/** POST /api/v3/incidents/attachments/decision/create — загрузить вложение решения (multipart/form-data) */
export const createDecisionAttachment = (
  accessToken: string,
  params: IncidentDecisionAttachmentUploadParams
): Promise<IncidentDecisionAttachment> => {
  const url = `${API_BASE_URL}/incidents/attachments/decision/create`

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && params.onProgress) {
        params.onProgress(Math.round((event.loaded / event.total) * 100))
      }
    })

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: POST /incidents/attachments/decision/create → ${xhr.status}`)
          debugLog('api', '📊 Status:', xhr.status)
          debugLog('api', '✅ OK:', true)
          debugGroupEnd('api')
        }
        try {
          resolve(JSON.parse(xhr.responseText))
        } catch {
          reject(new Error('Ошибка парсинга ответа сервера'))
        }
      } else {
        if (DEBUG.shouldLog('api')) {
          debugGroup('api', `📨 API v3 RESPONSE: POST /incidents/attachments/decision/create → ${xhr.status}`)
          debugLog('api', '📊 Status:', xhr.status)
          debugLog('api', '✅ OK:', false)
          debugLog('api', '❌ Error Body:', xhr.responseText)
          debugGroupEnd('api')
        }
        reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText || xhr.statusText}`))
      }
    })

    xhr.addEventListener('error', () => reject(new Error('Ошибка сетевого соединения')))
    xhr.addEventListener('abort', () => reject(new Error('Загрузка отменена')))

    const formData = new FormData()
    formData.append('File', params.file)
    formData.append('Index', String(params.index))
    formData.append('IncidentId', String(params.incidentId))

    if (DEBUG.shouldLog('api')) {
      debugGroup('api', `🚀 API v3 REQUEST: POST /incidents/attachments/decision/create`)
      debugLog('api', '📍 URL:', url)
      debugLog('api', '📦 File:', params.file.name, 'size:', params.file.size)
      debugLog('api', '📄 IncidentId:', params.incidentId, 'Index:', params.index)
      debugGroupEnd('api')
    }
    xhr.open('POST', url, true)
    xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`)
    xhr.send(formData)
  })
}

/** DELETE /api/v3/incidents/attachments/decision/{id} — удалить вложение решения */
export const deleteDecisionAttachment = async (
  accessToken: string,
  attachmentId: number
): Promise<void> => {
  return apiRequest(`/incidents/attachments/decision/${attachmentId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/{incidentId}/versions — дерево версий */
export const getIncidentVersions = async (
  accessToken: string,
  incidentId: number
): Promise<VersionTree> => {
  return apiRequest(`/incidents/${incidentId}/versions`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/versions/snapshots/{snapshotId} — получить снапшот */
export const getIncidentVersionSnapshot = async (
  accessToken: string,
  incidentId: number,
  snapshotId: number
): Promise<CommitSnapshot> => {
  return apiRequest(`/incidents/${incidentId}/versions/snapshots/${snapshotId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/{incidentId}/versions/session — текущая сессия */
export const getIncidentCurrentSession = async (
  accessToken: string,
  incidentId: number
): Promise<EditSession> => {
  return apiRequest(`/incidents/${incidentId}/versions/session`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/{incidentId}/versions/commits/{commitId}/session — сессия коммита */
export const getIncidentCommitSession = async (
  accessToken: string,
  incidentId: number,
  commitId: number
): Promise<EditSession> => {
  return apiRequest(`/incidents/${incidentId}/versions/commits/${commitId}/session`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/versions/start — начать сессию редактирования */
export const startIncidentEditSession = async (
  accessToken: string,
  incidentId: number
): Promise<void> => {
  return apiRequest(`/incidents/${incidentId}/versions/start`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/versions/complete — завершить сессию редактирования */
export const completeIncidentEditSession = async (
  accessToken: string,
  incidentId: number
): Promise<void> => {
  return apiRequest(`/incidents/${incidentId}/versions/complete`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/{incidentId}/versions/revert — откатить к коммиту */
export const revertIncidentCommit = async (
  accessToken: string,
  incidentId: number,
  commitId: number
): Promise<IncidentV3> => {
  return apiRequest(`/incidents/${incidentId}/versions/revert?commit=${commitId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ============================================================
// 🏷️ ТИПЫ ИНЦИДЕНТОВ (Incident Types API v3)
// ============================================================

export interface IncidentTypeCreateRequest {
  name: string
  workspaceId: number
}

export interface IncidentTypeUpdateRequest {
  name?: string | null
}

/** GET /api/v3/incidents/types — список типов инцидентов */
export const getIncidentTypes = async (
  accessToken: string,
  params: { workspace?: number } = {}
): Promise<IncidentTypeV3[]> => {
  const qs = params.workspace !== undefined ? `?workspace=${params.workspace}` : ''
  return apiRequest(`/incidents/types${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/incidents/types/{id} — один тип */
export const getIncidentType = async (
  accessToken: string,
  id: number
): Promise<IncidentTypeV3> => {
  return apiRequest(`/incidents/types/${id}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** DELETE /api/v3/incidents/types/{id} — удалить тип */
export const deleteIncidentType = async (
  accessToken: string,
  id: number
): Promise<void> => {
  return apiRequest(`/incidents/types/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/types/create — создать тип */
export const createIncidentType = async (
  accessToken: string,
  data: IncidentTypeCreateRequest
): Promise<IncidentTypeV3> => {
  return apiRequest('/incidents/types/create', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /api/v3/incidents/types/{id}/update — обновить тип */
export const updateIncidentType = async (
  accessToken: string,
  id: number,
  data: IncidentTypeUpdateRequest
): Promise<IncidentTypeV3> => {
  return apiRequest(`/incidents/types/${id}/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ─── Поля типа инцидента ────────────────────────────────────────────────────

export interface IncidentTypeFieldCreateRequest {
  name: string
  /** 1=Text, 2=Number, 3=SingleSelect, 4=MultiSelect, 5=Attachment, 6=User */
  fieldType: number
  required?: boolean
  index?: number
  options?: unknown | null
}

export interface IncidentTypeFieldUpdateRequest {
  name?: string | null
  /** 1=Text, 2=Number, 3=SingleSelect, 4=MultiSelect, 5=Attachment, 6=User */
  fieldType?: number | null
  required?: boolean | null
  index?: number | null
  options?: unknown | null
}

/** POST /api/v3/incidents/types/{typeId}/fields/create — создать поле типа */
export const createIncidentTypeField = async (
  accessToken: string,
  typeId: number,
  data: IncidentTypeFieldCreateRequest
): Promise<IncidentTypeFieldV3> => {
  return apiRequest(`/incidents/types/${typeId}/fields/create`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /api/v3/incidents/types/{typeId}/fields/{fieldId}/update — обновить поле */
export const updateIncidentTypeField = async (
  accessToken: string,
  typeId: number,
  fieldId: number,
  data: IncidentTypeFieldUpdateRequest
): Promise<IncidentTypeFieldV3> => {
  return apiRequest(`/incidents/types/${typeId}/fields/${fieldId}/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** DELETE /api/v3/incidents/types/{typeId}/fields/{fieldId} — удалить поле */
export const deleteIncidentTypeField = async (
  accessToken: string,
  typeId: number,
  fieldId: number
): Promise<void> => {
  return apiRequest(`/incidents/types/${typeId}/fields/${fieldId}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/incidents/types/{typeId}/fields/indexes — обновить порядок полей */
export const updateIncidentTypeFieldIndexes = async (
  accessToken: string,
  typeId: number,
  fieldIds: number[]
): Promise<void> => {
  return apiRequest(`/incidents/types/${typeId}/fields/indexes`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(fieldIds),
  })
}

// ===============================
// 🏷️ Scenario Types
// ===============================

export interface ScenarioTypeCreateRequest {
  name: string
  workspaceId: number
}

export interface ScenarioTypeUpdateRequest {
  name?: string | null
}

/** GET /api/v3/scenarios/types — список типов процедур */
export const getScenarioTypes = async (
  accessToken: string,
  params: { workspace?: number } = {}
): Promise<ScenarioTypeV3[]> => {
  const qs = params.workspace !== undefined ? `?workspace=${params.workspace}` : ''
  return apiRequest(`/scenarios/types${qs}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/scenarios/types/{id} — один тип */
export const getScenarioType = async (
  accessToken: string,
  id: number
): Promise<ScenarioTypeV3> => {
  return apiRequest(`/scenarios/types/${id}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** DELETE /api/v3/scenarios/types/{id} — удалить тип */
export const deleteScenarioType = async (
  accessToken: string,
  id: number
): Promise<void> => {
  return apiRequest(`/scenarios/types/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/scenarios/types/create — создать тип */
export const createScenarioType = async (
  accessToken: string,
  data: ScenarioTypeCreateRequest
): Promise<ScenarioTypeV3> => {
  return apiRequest('/scenarios/types/create', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/** POST /api/v3/scenarios/types/{id}/update — обновить тип */
export const updateScenarioType = async (
  accessToken: string,
  id: number,
  data: ScenarioTypeUpdateRequest
): Promise<ScenarioTypeV3> => {
  return apiRequest(`/scenarios/types/${id}/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

// ===============================
// ⚙️ System Settings
// ===============================

/** GET /api/v3/system/keys — получить все системные настройки */
export const getSystemKeys = async (accessToken: string): Promise<Record<string, string>> => {
  return apiRequest('/system/keys', {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** GET /api/v3/system/value?key=... — получить системную настройку по ключу */
export const getSystemValue = async (accessToken: string, key: string): Promise<string> => {
  return apiRequest(`/system/value?key=${encodeURIComponent(key)}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/** POST /api/v3/system/value?key=... — установить системную настройку по ключу */
export const setSystemValue = async (accessToken: string, key: string, value: string): Promise<void> => {
  return apiRequest(`/system/value?key=${encodeURIComponent(key)}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'text/plain',
    },
    body: value,
  })
}

export interface AnalyticsEventPayload {
  name: string
  timeZone: number
  type: number
  value: string | null
  workspaceId: number | null
}

/**
 * POST /analytics/event — отправить аналитическое событие.
 * fire-and-forget: ошибки логируются, но не пробрасываются.
 */
export async function trackAnalyticsEvent(
  accessToken: string,
  payload: AnalyticsEventPayload,
): Promise<void> {
  try {
    await apiRequest('/analytics/event', {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: JSON.stringify(payload),
    })
  } catch (e: any) {
    debugError('api', '⚠️ Analytics event failed:', e)
  }
}

// ─── Notifications ────────────────────────────────────────────────────────────

/** Тип уведомления: 0 — Default, 1 — Warning, 2 — Error, 3 — Exception */
export type NotificationType = 0 | 1 | 2 | 3

export interface NotificationCreateRequest {
  /** ID воркспейса */
  workspaceId: number
  /** Заголовок уведомления */
  title: string
  /** Текст уведомления */
  text: string
  /** Дополнительные данные (произвольный объект) */
  data?: Record<string, unknown> | null
  /** Тип уведомления */
  type?: NotificationType
  /** Список ID пользователей-получателей */
  userIds: number[]
}

export interface NotificationV3 {
  id: number
  title: string
  text: string
  data?: string | null
  type: NotificationType
  workspaceId: number
  creatorId?: number | null
  created: string
  updated: string
}

export interface UserNotificationV3 {
  userId: number
  notificationId: number
  notification?: NotificationV3 | null
  isRead: boolean
  readAt?: string | null
  isSendFailed: boolean
  errorText?: string | null
  created: string
  updated: string
}

/**
 * POST /api/v3/notifications — создать уведомление и доставить указанным пользователям.
 * Бросает ApiError при неудаче.
 * Может вернуть null если сервер ответил 200 OK без тела (HTTP/2 race condition).
 */
export async function createNotification(
  accessToken: string,
  data: NotificationCreateRequest,
): Promise<NotificationV3 | null> {
  return apiRequest('/notifications', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * GET /api/v3/workspaces/{workspaceId}/notifications — список уведомлений текущего
 * пользователя в воркспейсе (пагинация skip/take).
 */
export async function getWorkspaceNotifications(
  accessToken: string,
  workspaceId: number,
  params: { skip?: number; take?: number } = {},
): Promise<UserNotificationV3[]> {
  const qs = new URLSearchParams()
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  if (params.take !== undefined) qs.set('take', String(params.take))
  const query = qs.toString() ? `?${qs}` : ''
  return apiRequest(`/workspaces/${workspaceId}/notifications${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * POST /api/v3/notifications/{notificationId}/read — отметить уведомление прочитанным.
 */
export async function markNotificationAsRead(
  accessToken: string,
  notificationId: number,
): Promise<void> {
  return apiRequest(`/notifications/${notificationId}/read`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * POST /api/v3/notifications/read-all?workspace={workspaceId} — отметить все уведомления
 * текущего пользователя в воркспейсе прочитанными.
 */
export async function markAllNotificationsAsRead(
  accessToken: string,
  workspaceId: number,
): Promise<void> {
  return apiRequest(`/notifications/read-all?workspace=${workspaceId}`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

// ─── Scenario Assignments ──────────────────────────────────────────────────────

/**
 * Статус назначения процедуры (ScenarioAssignmentStatus в OpenAPI).
 * 0 - None; 1 - Pending; 2 - InProgress; 3 - AwaitApproval; 4 - Resolved.
 */
export enum ScenarioAssignmentStatus {
  None = 0,
  Pending = 1,
  InProgress = 2,
  AwaitApproval = 3,
  Resolved = 4,
}

/**
 * Назначение процедуры конкретному пользователю.
 * scenarioReportId/fullScenarioReport заполняются, как только прохождение стартовало
 * (не только по завершении).
 */
export interface ScenarioAssignment {
  id: number
  scenarioId: number
  /** ID ответственного пользователя (кому назначена процедура) */
  responsibleUserId: number
  /** Плановая дата выполнения (UTC Unix мс), null если не задана */
  scheduledAt?: number | null
  /** Дедлайн выполнения (UTC Unix мс), null если не задан */
  deadlineAt?: number | null
  /** Текущий статус назначения */
  status?: ScenarioAssignmentStatus
  /** Ссылка на ScenarioReport — заполняется, как только прохождение стартовало (не только при завершении) */
  scenarioReportId?: number | null
  /**
   * Полный отчёт прохождения (если есть связанный отчёт) — приходит сразу с назначением,
   * включая dataJson, поэтому источник реального прогресса и просмотра отчёта без
   * отдельного запроса (работает и для незавершённых прохождений, в отличие от
   * workspace reports-with-snapshots, где такие отчёты не отображаются).
   */
  fullScenarioReport?: FullScenarioReport | null
  name: string
  description?: string | null
  creatorId?: number | null
  creator?: { id?: number; name?: string; login?: string } | null
  created: string
  updated: string
  objectState?: number
  objectStatusId?: number | null
  version?: number
  activityCount?: number
}

/**
 * Назначение фактически завершено: либо сервер уже перевёл его в Resolved, либо
 * связанный отчёт отправлен (status=Sent), а перевод назначения в Resolved не дошёл
 * (например, прохождение продолжили без привязки к назначению).
 */
export function isAssignmentEffectivelyResolved(a: ScenarioAssignment): boolean {
  return a.status === ScenarioAssignmentStatus.Resolved
    || a.fullScenarioReport?.status === ScenarioReportStatus.Sent
}

/**
 * Реальный процент прохождения назначения — берётся из completedSteps/totalSteps
 * связанного отчёта (fullScenarioReport). Если назначение завершено (Resolved) —
 * считаем 100%. Если отчёт ещё не создан или данных о шагах нет — 0%.
 */
export function getAssignmentProgressPercent(a: ScenarioAssignment): number {
  if (isAssignmentEffectivelyResolved(a)) return 100
  const completed = a.fullScenarioReport?.completedSteps
  const total = a.fullScenarioReport?.totalSteps
  if (completed == null || total == null || total <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((completed / total) * 100)))
}

export interface ScenarioAssignmentCreateRequest {
  /** ID пользователя, которому назначаем процедуру */
  responsibleUserId: number
  /** Плановая дата (UTC Unix мс) */
  scheduledAt?: number | null
  /** Дедлайн (UTC Unix мс) */
  deadlineAt?: number | null
  status?: ScenarioAssignmentStatus | null
  name?: string | null
  description?: string | null
}

export interface ScenarioAssignmentUpdateRequest {
  /** Сменить исполнителя */
  responsibleUserId?: number | null
  /** Новая плановая дата (UTC Unix мс) */
  scheduledAt?: number | null
  /** Явно сбросить scheduledAt в null */
  clearScheduledAt?: boolean
  /** Новый дедлайн (UTC Unix мс) */
  deadlineAt?: number | null
  /** Явно сбросить deadlineAt в null */
  clearDeadlineAt?: boolean
  status?: ScenarioAssignmentStatus | null
  name?: string | null
  description?: string | null
  /**
   * ID real-time отчёта, который нужно привязать к назначению.
   * Передай null, чтобы очистить существующую привязку (снять связь с отчётом).
   */
  reportId?: number | null
}

export interface GetScenarioAssignmentsParams {
  skip?: number
  take?: number
  /** Фильтр: только выполненные (есть scenarioReportId) или нет */
  completed?: boolean
  /** Фильтр по одному исполнителю */
  responsibleUserId?: number
  /** Фильтр по нескольким исполнителям */
  assignedTo?: number[]
  /** Фильтр по тем, кто назначил */
  assignedBy?: number[]
  /** Фильтр по статусам назначения */
  statuses?: ScenarioAssignmentStatus[]
  /** Только назначенные текущему авторизованному пользователю */
  assignedToMe?: boolean
}

/**
 * GET /api/v3/scenarios/{scenarioId}/assignments — список назначений процедуры.
 * Поддерживает пагинацию и фильтрацию.
 */
export const getScenarioAssignments = async (
  accessToken: string,
  scenarioId: number,
  params: GetScenarioAssignmentsParams = {},
): Promise<ScenarioAssignment[]> => {
  const qs = new URLSearchParams()
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  if (params.take !== undefined) qs.set('take', String(params.take))
  if (params.completed !== undefined) qs.set('completed', String(params.completed))
  if (params.responsibleUserId !== undefined) qs.set('responsibleUserId', String(params.responsibleUserId))
  if (params.assignedTo?.length) params.assignedTo.forEach(id => qs.append('assignedTo', String(id)))
  if (params.assignedBy?.length) params.assignedBy.forEach(id => qs.append('assignedBy', String(id)))
  if (params.statuses?.length) params.statuses.forEach(s => qs.append('statuses', String(s)))
  if (params.assignedToMe !== undefined) qs.set('assignedToMe', String(params.assignedToMe))
  const query = qs.toString() ? `?${qs}` : ''
  return apiRequest(`/scenarios/${scenarioId}/assignments${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * Статусы назначения, которые считаются «незавершёнными» (всё, кроме Resolved).
 * Используется как фильтр `statuses` для вкладки «Назначено».
 */
export const UNRESOLVED_ASSIGNMENT_STATUSES: ScenarioAssignmentStatus[] = [
  ScenarioAssignmentStatus.None,
  ScenarioAssignmentStatus.Pending,
  ScenarioAssignmentStatus.InProgress,
  ScenarioAssignmentStatus.AwaitApproval,
]

/**
 * Находит незавершённое назначение, к которому уже привязан отчёт reportId.
 * Нужен при продолжении прохождения по ?reportId= (без ?assignmentId=: после старта
 * он убирается из URL), иначе отправка отчёта не переведёт назначение в Resolved.
 */
export const findAssignmentIdByReportId = async (
  accessToken: string,
  scenarioId: number,
  reportId: number,
): Promise<number | undefined> => {
  try {
    const assignments = await getScenarioAssignments(accessToken, scenarioId, {
      statuses: UNRESOLVED_ASSIGNMENT_STATUSES,
      take: 200,
    })
    return assignments.find((a) => a.scenarioReportId != null && Number(a.scenarioReportId) === reportId)?.id
  } catch {
    return undefined
  }
}

/**
 * Статусы назначения, которые считаются «завершёнными» — используется как фильтр
 * `statuses` для вкладки «Завершено».
 */
export const RESOLVED_ASSIGNMENT_STATUSES: ScenarioAssignmentStatus[] = [
  ScenarioAssignmentStatus.Resolved,
]

/**
 * GET /api/v3/assignments — список назначений текущего пользователя/воркспейса
 * без привязки к конкретной процедуре. Заменяет перебор всех процедур с вызовом
 * getScenarioAssignments по каждому — один запрос вместо N.
 * Поддерживает те же фильтры и пагинацию (skip/take), что и per-scenario вариант.
 */
export const getAllScenarioAssignments = async (
  accessToken: string,
  params: GetScenarioAssignmentsParams = {},
): Promise<ScenarioAssignment[]> => {
  const qs = new URLSearchParams()
  if (params.skip !== undefined) qs.set('skip', String(params.skip))
  if (params.take !== undefined) qs.set('take', String(params.take))
  if (params.completed !== undefined) qs.set('completed', String(params.completed))
  if (params.responsibleUserId !== undefined) qs.set('responsibleUserId', String(params.responsibleUserId))
  if (params.assignedTo?.length) params.assignedTo.forEach(id => qs.append('assignedTo', String(id)))
  if (params.assignedBy?.length) params.assignedBy.forEach(id => qs.append('assignedBy', String(id)))
  if (params.statuses?.length) params.statuses.forEach(s => qs.append('statuses', String(s)))
  if (params.assignedToMe !== undefined) qs.set('assignedToMe', String(params.assignedToMe))
  const query = qs.toString() ? `?${qs}` : ''
  return apiRequest(`/assignments${query}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * GET /api/v3/scenarios/{scenarioId}/assignments/{id} — получить одно назначение.
 */
export const getScenarioAssignment = async (
  accessToken: string,
  scenarioId: number,
  id: number,
): Promise<ScenarioAssignment> => {
  return apiRequest(`/scenarios/${scenarioId}/assignments/${id}`, {
    method: 'GET',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}

/**
 * POST /api/v3/scenarios/{scenarioId}/assignments/create — создать назначение.
 */
export const createScenarioAssignment = async (
  accessToken: string,
  scenarioId: number,
  data: ScenarioAssignmentCreateRequest,
): Promise<ScenarioAssignment> => {
  return apiRequest(`/scenarios/${scenarioId}/assignments/create`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * POST /api/v3/scenarios/{scenarioId}/assignments/{id}/update — обновить назначение.
 * Для сброса дат используй clearScheduledAt / clearDeadlineAt = true.
 */
export const updateScenarioAssignment = async (
  accessToken: string,
  scenarioId: number,
  id: number,
  data: ScenarioAssignmentUpdateRequest,
): Promise<ScenarioAssignment> => {
  return apiRequest(`/scenarios/${scenarioId}/assignments/${id}/update`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  })
}

/**
 * DELETE /api/v3/scenarios/{scenarioId}/assignments/{id} — удалить назначение.
 */
export const deleteScenarioAssignment = async (
  accessToken: string,
  scenarioId: number,
  id: number,
): Promise<void> => {
  return apiRequest(`/scenarios/${scenarioId}/assignments/${id}`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${accessToken}` },
  })
}
